import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertTriangle,
  ArrowLeft,
  BadgeDollarSign,
  BarChart3,
  Bell,
  Building2,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Copy,
  Database,
  Edit3,
  Eye,
  GripVertical,
  LayoutDashboard,
  Link2,
  ListChecks,
  Loader2,
  LogOut,
  Maximize2,
  Menu,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  RotateCcw,
  Search,
  Settings,
  Sparkles,
  Trash2,
  UserRound,
  Users,
  X
} from "lucide-react";
import type {
  Activity,
  CrmState,
  DashboardLayout,
  DashboardWidgetId,
  InterestedProperty,
  Lead,
  MasterData,
  Property,
  PropertyMatch,
  Requirement,
  Role,
  UserProfile,
  Viewing
} from "./types";
import {
  addAudit,
  defaultDashboardWidgets,
  defaultState,
  getSession,
  loadState,
  saveState,
  signInWithGoogle,
  signOut
} from "./lib/data";
import { HIGH_MATCH_THRESHOLD, rankProperties } from "./lib/matching";
import { runtime, isSupabaseConfigured } from "./lib/runtime";
import { cn } from "./lib/utils";
import { dateTime, id, money, percent, todayIso } from "./lib/utils";

type View = "dashboard" | "leads" | "leadDetail" | "properties" | "propertyDetail" | "scheduleDetail" | "followupDetail" | "configuration";
type SaveStatus = "Loading" | "Saving" | "Saved" | "Error" | string;
type ScheduleDraft = { leadId: string; propertyId: string; interestId?: string; type: string; date: string; time: string };

const nav: { id: View; label: string; icon: typeof LayoutDashboard; roles: Role[] }[] = [
  { id: "dashboard", label: "dashboard", icon: LayoutDashboard, roles: ["Admin", "Manager", "Agent"] },
  { id: "leads", label: "leads", icon: Users, roles: ["Admin", "Manager", "Agent"] },
  { id: "properties", label: "properties", icon: Building2, roles: ["Admin", "Manager", "Agent"] },
  { id: "configuration", label: "configuration", icon: Settings, roles: ["Admin"] }
];

const viewTitles: Record<View, string> = {
  dashboard: "Dashboard",
  leads: "Leads",
  leadDetail: "Lead Detail",
  properties: "Properties",
  propertyDetail: "Property Detail",
  scheduleDetail: "Schedule Detail",
  followupDetail: "Follow-up Detail",
  configuration: "Configuration"
};

const masterLabels: Record<string, string> = {
  propertyPurposes: "Property Purposes",
  propertyDevelopments: "Property Developments",
  propertyTypes: "Property Types",
  propertyStatuses: "Property Statuses",
  propertySources: "Property Sources",
  leadSources: "Lead Sources",
  leadStatuses: "Lead Statuses",
  leadTemperatures: "Lead Temperatures",
  nationalities: "Nationalities",
  btsStations: "BTS Stations",
  mrtStations: "MRT Stations",
  airportLinkStations: "Airport Link Stations",
  airports: "Airports",
  facilities: "Facilities",
  roomTypes: "Room Types",
  parkingTypes: "Parking Types",
  interestStatuses: "Interest Statuses",
  scheduleTypes: "Schedule Types",
  scheduleStatuses: "Schedule Statuses",
  viewingStatuses: "Viewing Statuses",
  activityTypes: "Activity Types",
  activityStatuses: "Activity Statuses",
  pipelineStages: "Pipeline Stages",
  paymentStatuses: "Payment Statuses",
  teams: "Teams",
  userRoles: "User Roles"
};

const masterUsageLabels: Record<string, string[]> = {
  propertyPurposes: ["Lead requirement", "Property detail", "Matching"],
  propertyDevelopments: ["Lead requirement", "Property detail", "Property list"],
  propertyTypes: ["Lead requirement", "Property detail", "Property list"],
  propertyStatuses: ["Property pipeline", "Property detail", "Dashboard"],
  propertySources: ["Property detail", "Property search"],
  leadSources: ["Lead profile", "Lead search"],
  leadStatuses: ["Lead pipeline", "Lead profile", "Dashboard"],
  leadTemperatures: ["Lead profile", "Lead list"],
  nationalities: ["Lead profile", "Lead search"],
  btsStations: ["Lead requirement", "Property detail", "Matching"],
  mrtStations: ["Lead requirement", "Property detail", "Matching"],
  airportLinkStations: ["Lead requirement", "Matching"],
  airports: ["Lead requirement", "Matching"],
  facilities: ["Lead requirement", "Property detail", "Matching"],
  roomTypes: ["Lead requirement", "Property detail"],
  parkingTypes: ["Lead requirement", "Property detail"],
  interestStatuses: ["Interested properties", "Lead detail", "Property detail"],
  scheduleTypes: ["Viewing schedule", "Follow-up schedule"],
  scheduleStatuses: ["Viewing schedule", "Schedule detail"],
  viewingStatuses: ["Viewing schedule", "Lead detail", "Property detail"],
  activityTypes: ["Follow-up schedule", "Lead detail"],
  activityStatuses: ["Follow-up schedule", "Dashboard"],
  pipelineStages: ["Pipeline reporting", "Opportunity tracking"],
  paymentStatuses: ["Commissions", "Finance tracking"],
  teams: ["Users", "Data access", "Lead assignment"],
  userRoles: ["Users", "Permissions", "Sidebar access"]
};

const masterUsageMetaKey = "__masterFieldUsage";
const masterConnectionOptions = Array.from(new Set(Object.values(masterUsageLabels).flat().concat(["Dashboard", "Configuration", "Custom workflow"]))).sort(sortText);

const widgetLabels: Record<DashboardWidgetId, string> = {
  conversion: "Conversion Rate",
  commission: "Gained Commission",
  topLeads: "Top 3 Potential Leads",
  topProperties: "Top 3 High Potential Properties",
  schedule: "Schedule / Calendar",
  activity: "Recent Activities"
};

function masterLabel(key: string) {
  return masterLabels[key] || key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function masterUsage(key: string, masterData?: MasterData) {
  const dynamic = masterData?.[masterUsageMetaKey]
    ?.map((item) => {
      const [fieldKey, section] = item.split("::");
      return fieldKey === key ? section : "";
    })
    .filter(Boolean) || [];
  return dynamic.length ? dynamic : masterUsageLabels[key] || ["Custom configuration"];
}

function createMasterKey(label: string, masterData: MasterData) {
  const words = label.trim().replace(/[^a-zA-Z0-9]+/g, " ").split(" ").filter(Boolean);
  const fallback = `customField${Object.keys(masterData).length + 1}`;
  const base = words.length
    ? words.map((word, index) => {
      const normalized = word.toLowerCase();
      return index === 0 ? normalized : `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
    }).join("")
    : fallback;
  let key = base;
  let suffix = 2;
  while (Object.prototype.hasOwnProperty.call(masterData, key)) {
    key = `${base}${suffix}`;
    suffix += 1;
  }
  return key;
}

function sortText(a: string, b: string) {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

type SortDirection = "asc" | "desc";
type RequirementFilterKey = keyof Pick<
  Requirement,
  | "purpose"
  | "location"
  | "preferredAreas"
  | "nearBts"
  | "nearMrt"
  | "nearAirportLink"
  | "nearAirport"
  | "propertyType"
  | "development"
  | "budgetMin"
  | "budgetMax"
  | "bedrooms"
  | "bathrooms"
  | "livingRooms"
  | "kitchens"
  | "otherRooms"
  | "size"
  | "parkingCars"
  | "parkingMotorcycles"
  | "parkingBicycles"
  | "facilities"
  | "timeline"
>;

const requirementFilterFields: { key: RequirementFilterKey; label: string }[] = [
  { key: "purpose", label: "Purpose" },
  { key: "location", label: "Location" },
  { key: "preferredAreas", label: "Preferred Areas" },
  { key: "nearBts", label: "Near BTS" },
  { key: "nearMrt", label: "Near MRT" },
  { key: "nearAirportLink", label: "Near Airport Link" },
  { key: "nearAirport", label: "Near Airport" },
  { key: "propertyType", label: "Property Type" },
  { key: "development", label: "Development" },
  { key: "budgetMin", label: "Budget Min" },
  { key: "budgetMax", label: "Budget Max" },
  { key: "bedrooms", label: "Bedrooms" },
  { key: "bathrooms", label: "Bathrooms" },
  { key: "livingRooms", label: "Living Rooms" },
  { key: "kitchens", label: "Kitchens" },
  { key: "otherRooms", label: "Other Rooms" },
  { key: "size", label: "Size" },
  { key: "parkingCars", label: "Car Parking" },
  { key: "parkingMotorcycles", label: "Motorcycle Parking" },
  { key: "parkingBicycles", label: "Bicycle Parking" },
  { key: "facilities", label: "Facilities" },
  { key: "timeline", label: "Timeline" }
];

function compareValues(a: string | number | boolean | undefined, b: string | number | boolean | undefined, direction: SortDirection) {
  const left = typeof a === "number" ? a : String(a ?? "");
  const right = typeof b === "number" ? b : String(b ?? "");
  const result = typeof left === "number" && typeof right === "number" ? left - right : sortText(String(left), String(right));
  return direction === "asc" ? result : -result;
}

function datePart(value: string) {
  return value ? value.slice(0, 10) : todayIso();
}

function timePart(value: string) {
  return value ? value.slice(11, 16) || "10:00" : "10:00";
}

function combineDateTime(date: string, time: string) {
  return `${date || todayIso()}T${time || "10:00"}`;
}

function listFromText(value: string | undefined) {
  return String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
}

function textFromList(values: string[]) {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort(sortText).join(", ");
}

function requirementValue(requirement: Requirement, key: RequirementFilterKey) {
  return String(requirement[key] ?? "");
}

function requirementPassesFilter(requirements: Requirement[], key: RequirementFilterKey, value: string) {
  if (!value.trim()) return true;
  const needle = value.toLowerCase();
  return requirements.some((requirement) => requirementValue(requirement, key).toLowerCase().includes(needle));
}

function publicTokenFromUrl() {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash.match(/^#\/public\/([^/?]+)/);
  if (hash) return decodeURIComponent(hash[1]);
  return new URLSearchParams(window.location.search).get("propertyToken") || "";
}

function publicPropertyUrl(token: string) {
  if (typeof window === "undefined" || !token) return "";
  return `${window.location.origin}${window.location.pathname}#/public/${encodeURIComponent(token)}`;
}

function numberText(value: number) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function propertyViewChoices(property: Property) {
  return listFromText(property.view || [property.poolView && "Pool", property.gardenView && "Garden"].filter(Boolean).join(", "));
}

function propertyFacilities(property: Property) {
  return listFromText(property.facilities);
}

function isSoldOutStatus(status: string) {
  return status.toLowerCase().replace(/\s+/g, "-") === "sold-out";
}

function isClosedLeadStatus(status: string) {
  return ["closed-won", "closed-lost"].includes(status.toLowerCase().replace(/\s+/g, "-"));
}

function matchableProperties(properties: Property[]) {
  return properties.filter((property) => !isSoldOutStatus(property.status));
}

function matchableLeads(leads: Lead[]) {
  return leads.filter((lead) => !isClosedLeadStatus(lead.status));
}

function userTeams(user: UserProfile) {
  return user.teamIds?.length ? user.teamIds : user.teamId ? [user.teamId] : [];
}

function emptyLead(currentUser: UserProfile): Lead {
  const now = new Date().toISOString();
  return {
    id: id("lead"),
    name: "",
    phone: "",
    email: "",
    nationality: "Thai",
    source: "Website",
    purpose: "Buy",
    budget: 0,
    timeline: "",
    temperature: "Warm",
    status: "New",
    note: "",
    agentId: currentUser.id,
    teamId: currentUser.teamId,
    active: true,
    createdBy: currentUser.id,
    updatedBy: currentUser.id,
    createdAt: now,
    updatedAt: now
  };
}

function emptyRequirement(leadId: string, masterData: MasterData, lead?: Lead): Requirement {
  return {
    id: id("req"),
    leadId,
    purpose: lead?.purpose || masterData.propertyPurposes?.[0] || "Buy",
    location: "Bangkok",
    preferredAreas: "",
    nearBts: "",
    nearMrt: "",
    nearAirportLink: "",
    nearAirport: "",
    propertyType: masterData.propertyTypes?.[0] || "Condo",
    development: "",
    budgetMin: 0,
    budgetMax: lead?.budget || 0,
    currency: "THB",
    bedrooms: 1,
    bathrooms: 1,
    livingRooms: 1,
    kitchens: 1,
    otherRooms: 0,
    size: 0,
    parkingCars: 0,
    parkingMotorcycles: 0,
    parkingBicycles: 0,
    petFriendly: false,
    transitAccess: true,
    pool: false,
    fitness: false,
    coWorkingSpace: false,
    garden: false,
    facilities: "",
    timeline: lead?.timeline || "",
    updatedAt: new Date().toISOString()
  };
}

function emptyProperty(masterData: MasterData, currentUser: UserProfile): Property {
  const now = new Date().toISOString();
  return {
    id: id("prop"),
    code: `HL-${Math.floor(1000 + Math.random() * 9000)}`,
    name: "",
    purpose: masterData.propertyPurposes?.[0] || "Buy",
    type: masterData.propertyTypes?.[0] || "Condo",
    development: "",
    building: "",
    floor: "",
    location: "",
    nearBts: "",
    nearMrt: "",
    nearAirportLink: "",
    nearAirport: "",
    source: masterData.propertySources?.[0] || "Owner Direct",
    status: masterData.propertyStatuses?.[0] || "Available",
    originalPrice: 0,
    promotionPrice: 0,
    price: 0,
    currency: "THB",
    size: 0,
    bedrooms: 1,
    bathrooms: 1,
    livingRooms: 1,
    kitchens: 1,
    otherRooms: 0,
    parkingCars: 0,
    parkingMotorcycles: 0,
    parkingBicycles: 0,
    petFriendly: false,
    pool: false,
    view: "",
    fitness: false,
    coWorkingSpace: false,
    garden: false,
    facilities: "",
    photos: "",
    timeline: "",
    commissionRate: 0,
    commissionAmount: 0,
    affiliateLink: "",
    soldOutAgentId: "",
    publicToken: "",
    publicVisible: false,
    visibility: "Private",
    internalNote: "",
    active: true,
    createdBy: currentUser.id,
    updatedBy: currentUser.id,
    createdAt: now,
    updatedAt: now
  };
}

function canViewLead(user: UserProfile, lead: Lead) {
  if (lead.active === false) return false;
  if (user.role === "Admin") return true;
  if (user.role === "Manager") return lead.agentId === user.id || user.teamIds.includes(lead.teamId);
  return lead.agentId === user.id;
}

function canEditLead(user: UserProfile, lead: Lead) {
  if (user.role === "Admin") return true;
  if (user.role === "Manager") return lead.agentId === user.id || user.teamIds.includes(lead.teamId);
  return lead.agentId === user.id;
}

function normalizeComparable(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function findLeadDuplicates(leads: Lead[], draft: Lead) {
  const phone = normalizeComparable(draft.phone);
  const email = normalizeComparable(draft.email);
  return leads.filter((lead) => lead.id !== draft.id && ((phone && normalizeComparable(lead.phone) === phone) || (email && normalizeComparable(lead.email) === email)));
}

function findPropertyDuplicates(properties: Property[], draft: Property) {
  return properties.filter((property) => {
    if (property.id === draft.id) return false;
    const sameDevelopment = normalizeComparable(property.development || property.name) === normalizeComparable(draft.development || draft.name);
    const sameUnit = String(property.building || "") === String(draft.building || "") && String(property.floor || "") === String(draft.floor || "");
    const sameShape = Number(property.size || 0) === Number(draft.size || 0) && Number(property.bedrooms || 0) === Number(draft.bedrooms || 0) && Number(property.bathrooms || 0) === Number(draft.bathrooms || 0);
    return sameDevelopment && sameUnit && sameShape;
  });
}

function scopeStateForUser(state: CrmState, user: UserProfile): CrmState {
  const visibleLeadIds = new Set(state.leads.filter((lead) => canViewLead(user, lead)).map((lead) => lead.id));
  const visibleOpportunityIds = new Set(state.opportunities.filter((opp) => visibleLeadIds.has(opp.leadId) || user.role === "Admin").map((opp) => opp.id));
  return {
    ...state,
    leads: state.leads.filter((lead) => visibleLeadIds.has(lead.id)),
    requirements: state.requirements.filter((requirement) => visibleLeadIds.has(requirement.leadId)),
    properties: state.properties.filter((property) => property.active !== false),
    interestedProperties: state.interestedProperties.filter((interest) => visibleLeadIds.has(interest.leadId) || user.role === "Admin"),
    viewings: state.viewings.filter((viewing) => visibleLeadIds.has(viewing.leadId) || viewing.agentId === user.id || user.role === "Admin"),
    activities: state.activities.filter((activity) => visibleLeadIds.has(activity.leadId) || activity.agentId === user.id || user.role === "Admin"),
    opportunities: state.opportunities.filter((opp) => visibleLeadIds.has(opp.leadId) || user.role === "Admin"),
    commissions: state.commissions.filter((commission) => visibleOpportunityIds.has(commission.opportunityId) || user.role === "Admin"),
    notifications: state.notifications.filter((notification) => notification.userId === user.id || user.role === "Admin")
  };
}

function isToday(value: string) {
  return value?.slice(0, 10) === todayIso();
}

function isOverdue(value: string) {
  return Boolean(value && new Date(value).getTime() < Date.now());
}

function nextAction(state: CrmState, requirements: Requirement[], properties: Property[]) {
  const overdue = state.activities.filter((activity) => activity.status !== "Completed" && isOverdue(activity.dueAt)).length;
  if (overdue) return `${numberText(overdue)} follow-up${overdue === 1 ? " is" : "s are"} overdue`;
  const todayViewings = state.viewings.filter((viewing) => isToday(viewing.startsAt) && viewing.status === "Scheduled").length;
  if (todayViewings) return `${numberText(todayViewings)} viewing${todayViewings === 1 ? "" : "s"} scheduled today`;
  const newLeads = matchableLeads(state.leads).filter((lead) => lead.status === "New").length;
  if (newLeads) return `${numberText(newLeads)} new lead${newLeads === 1 ? "" : "s"} in your scope`;
  const activeLeadIds = new Set(matchableLeads(state.leads).map((lead) => lead.id));
  const highMatches = requirements.filter((requirement) => activeLeadIds.has(requirement.leadId)).reduce((sum, requirement) => sum + rankProperties(requirement, matchableProperties(properties), 5).filter((property) => property.matchScore >= HIGH_MATCH_THRESHOLD).length, 0);
  if (highMatches) return `${numberText(highMatches)} high-match propert${highMatches === 1 ? "y" : "ies"} ready`;
  return "All priority work is clear";
}

function App() {
  const intl = useIntl();
  const [state, setState] = useState<CrmState>(() => defaultState());
  const [view, setView] = useState<View>("dashboard");
  const [selectedLeadId, setSelectedLeadId] = useState("lead-demo");
  const [selectedPropertyId, setSelectedPropertyId] = useState("prop-demo-1");
  const [currentUserId, setCurrentUserId] = useState("usr-admin");
  const [role, setRole] = useState<Role>("Admin");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<SaveStatus>("Loading");
  const [authMessage, setAuthMessage] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [publicToken, setPublicToken] = useState(() => publicTokenFromUrl());
  const [draftLead, setDraftLead] = useState<Lead | null>(null);
  const [draftProperty, setDraftProperty] = useState<Property | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft | null>(null);

  const currentUser = state.users.find((user) => user.id === currentUserId && user.active) || state.users.find((user) => user.active) || state.users[0];
  const effectiveUser = { ...currentUser, role };
  const scopedState = useMemo(() => scopeStateForUser(state, effectiveUser), [state, currentUser.id, role]);
  const visibleLeads = scopedState.leads;
  const visibleProperties = scopedState.properties;
  const selectedLead = draftLead?.id === selectedLeadId ? draftLead : visibleLeads.find((lead) => lead.id === selectedLeadId) || visibleLeads[0];
  const selectedProperty = draftProperty?.id === selectedPropertyId ? draftProperty : visibleProperties.find((property) => property.id === selectedPropertyId) || visibleProperties[0];
  const selectedRequirements = state.requirements.filter((requirement) => requirement.leadId === selectedLead?.id);
  const selectedRequirement = selectedRequirements[0];
  const layout = state.dashboardLayouts.find((item) => item.userId === currentUser.id) || {
    id: id("layout"),
    userId: currentUser.id,
    widgets: defaultDashboardWidgets,
    hiddenWidgets: [],
    updatedAt: new Date().toISOString()
  };

  useEffect(() => {
    let mounted = true;
    async function boot() {
      try {
        const session = await getSession();
        if (mounted && isSupabaseConfigured() && runtime.requireAuth && !session && !publicToken) {
          setStatus("Saved");
          setAuthMessage("Welcome back. Continue with Google to open your workspace.");
          return;
        }
        const next = await loadState();
        if (!mounted) return;
        setState(next);
        const profile = session ? next.users.find((user) => user.id === session.user.id) : undefined;
        const firstUser = profile || next.users.find((user) => user.active) || next.users[0];
        setCurrentUserId(firstUser.id);
        setRole(firstUser.role);
        setSelectedLeadId(next.leads[0]?.id || "");
        setSelectedPropertyId(next.properties[0]?.id || "");
        setAuthMessage("");
        setStatus(isSupabaseConfigured() ? (session ? "All changes saved" : "Local demo") : "Local demo");
      } catch (error) {
        if (!mounted) return;
        const message = error instanceof Error ? error.message : "Unable to load CRM data.";
        setStatus("Error");
        setAuthMessage(message);
      }
    }
    void boot();
    return () => {
      mounted = false;
    };
  }, [publicToken]);

  useEffect(() => {
    function syncPublicToken() {
      setPublicToken(publicTokenFromUrl());
    }
    window.addEventListener("hashchange", syncPublicToken);
    window.addEventListener("popstate", syncPublicToken);
    return () => {
      window.removeEventListener("hashchange", syncPublicToken);
      window.removeEventListener("popstate", syncPublicToken);
    };
  }, []);

  useEffect(() => {
    if (draftLead?.id !== selectedLeadId && visibleLeads.length && !visibleLeads.some((lead) => lead.id === selectedLeadId)) setSelectedLeadId(visibleLeads[0].id);
    if (draftProperty?.id !== selectedPropertyId && visibleProperties.length && !visibleProperties.some((property) => property.id === selectedPropertyId)) setSelectedPropertyId(visibleProperties[0].id);
  }, [visibleLeads, visibleProperties, selectedLeadId, selectedPropertyId, draftLead, draftProperty]);

  async function persist(next: CrmState, action = "All changes saved") {
    setState(next);
    setStatus("Saving");
    try {
      await saveState(next);
      setStatus(action);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Save failed. Please retry.");
    }
  }

  async function handleGoogleLogin() {
    try {
      setStatus("Loading");
      await signInWithGoogle();
    } catch (error) {
      setStatus("Error");
      setAuthMessage(error instanceof Error ? error.message : "Google login failed.");
    }
  }

  async function handleLogout() {
    await signOut();
    setStatus("Signed out");
    if (runtime.requireAuth && isSupabaseConfigured()) setAuthMessage("Welcome back. Continue with Google to open your workspace.");
  }

  function updateLead(lead: Lead) {
    const previous = state.leads.find((item) => item.id === lead.id);
    if (!lead.name.trim()) {
      setStatus("Lead name is required before adding to the pipeline.");
      return;
    }
    const assignedUser = state.users.find((user) => user.id === lead.agentId);
    const nextLead = {
      ...lead,
      teamId: assignedUser?.teamId || lead.teamId,
      active: true,
      updatedBy: currentUser.id,
      updatedAt: new Date().toISOString()
    };
    if (!previous) {
      const requirement = emptyRequirement(nextLead.id, state.masterData, nextLead);
      void persist(
        addAudit({ ...state, leads: [nextLead, ...state.leads], requirements: [requirement, ...state.requirements] }, "create", "lead", nextLead.id, currentUser.id, undefined, nextLead),
        "Lead added"
      );
      setDraftLead(null);
      setSelectedLeadId(nextLead.id);
      setView("leadDetail");
      return;
    }
    const next = addAudit(
      { ...state, leads: state.leads.map((item) => (item.id === lead.id ? nextLead : item)) },
      "update",
      "lead",
      lead.id,
      currentUser.id,
      previous,
      nextLead
    );
    void persist(next, "Lead saved");
  }

  function deleteLead(leadId: string) {
    const previous = state.leads.find((lead) => lead.id === leadId);
    if (!previous) {
      if (draftLead?.id === leadId) {
        setDraftLead(null);
        setSelectedLeadId(state.leads[0]?.id || "");
        setView("leads");
      }
      return;
    }
    const removedOpportunityIds = new Set(state.opportunities.filter((opportunity) => opportunity.leadId === leadId).map((opportunity) => opportunity.id));
    const nextState: CrmState = {
      ...state,
      leads: state.leads.filter((lead) => lead.id !== leadId),
      requirements: state.requirements.filter((requirement) => requirement.leadId !== leadId),
      interestedProperties: state.interestedProperties.filter((interest) => interest.leadId !== leadId),
      viewings: state.viewings.filter((viewing) => viewing.leadId !== leadId),
      activities: state.activities.filter((activity) => activity.leadId !== leadId),
      opportunities: state.opportunities.filter((opportunity) => opportunity.leadId !== leadId),
      commissions: state.commissions.filter((commission) => !removedOpportunityIds.has(commission.opportunityId))
    };
    const next = addAudit(nextState, "delete", "lead", leadId, currentUser.id, previous);
    void persist(next, "Lead deleted");
    const fallback = nextState.leads.find((lead) => canViewLead(effectiveUser, lead));
    setSelectedLeadId(fallback?.id || "");
    setView("leads");
  }

  function updateRequirement(requirement: Requirement) {
    const previous = state.requirements.find((item) => item.id === requirement.id);
    const nextRequirement = { ...requirement, updatedAt: new Date().toISOString() };
    const list = state.requirements.some((item) => item.id === requirement.id)
      ? state.requirements.map((item) => (item.id === requirement.id ? nextRequirement : item))
      : [...state.requirements, nextRequirement];
    void persist(addAudit({ ...state, requirements: list }, "update", "requirement", requirement.id, currentUser.id, previous, nextRequirement), "Requirement saved");
  }

  function updateProperty(property: Property) {
    const previous = state.properties.find((item) => item.id === property.id);
    if (!property.name.trim()) {
      setStatus("Property name is required before adding to the pipeline.");
      return;
    }
    const price = Number(property.promotionPrice || property.price || property.originalPrice || 0);
    const nextProperty = {
      ...property,
      price,
      commissionAmount: Math.round((price * Number(property.commissionRate || 0)) / 100),
      publicVisible: property.visibility === "Public" || property.publicVisible,
      active: true,
      updatedBy: currentUser.id,
      updatedAt: new Date().toISOString()
    };
    if (!previous) {
      void persist(
        addAudit({ ...state, properties: [nextProperty, ...state.properties] }, "create", "property", nextProperty.id, currentUser.id, undefined, nextProperty),
        "Property added"
      );
      setDraftProperty(null);
      setSelectedPropertyId(nextProperty.id);
      setView("propertyDetail");
      return;
    }
    const next = addAudit(
      { ...state, properties: state.properties.map((item) => (item.id === property.id ? nextProperty : item)) },
      "update",
      "property",
      property.id,
      currentUser.id,
      previous,
      nextProperty
    );
    void persist(next, "Property saved");
  }

  function deleteProperty(propertyId: string) {
    const previous = state.properties.find((property) => property.id === propertyId);
    if (!previous) {
      if (draftProperty?.id === propertyId) {
        setDraftProperty(null);
        setSelectedPropertyId(state.properties[0]?.id || "");
        setView("properties");
      }
      return;
    }
    const removedOpportunityIds = new Set(state.opportunities.filter((opportunity) => opportunity.propertyId === propertyId).map((opportunity) => opportunity.id));
    const nextState: CrmState = {
      ...state,
      properties: state.properties.filter((property) => property.id !== propertyId),
      interestedProperties: state.interestedProperties.filter((interest) => interest.propertyId !== propertyId),
      viewings: state.viewings.filter((viewing) => viewing.propertyId !== propertyId),
      activities: state.activities.filter((activity) => activity.propertyId !== propertyId),
      opportunities: state.opportunities.filter((opportunity) => opportunity.propertyId !== propertyId),
      commissions: state.commissions.filter((commission) => !removedOpportunityIds.has(commission.opportunityId))
    };
    const next = addAudit(nextState, "delete", "property", propertyId, currentUser.id, previous);
    void persist(next, "Property deleted");
    const fallback = nextState.properties.find((property) => property.active !== false);
    setSelectedPropertyId(fallback?.id || "");
    setView("properties");
  }

  function addLead() {
    const lead = emptyLead(effectiveUser);
    setDraftLead(lead);
    setSelectedLeadId(lead.id);
    setView("leadDetail");
    setStatus("Fill lead details, then Save to add it to the pipeline.");
  }

  function addProperty() {
    const property = emptyProperty(state.masterData, effectiveUser);
    setDraftProperty(property);
    setSelectedPropertyId(property.id);
    setView("propertyDetail");
    setStatus("Fill property details, then Save to add it to the pipeline.");
  }

  function updateInterest(interest: InterestedProperty) {
    const nextInterest = { ...interest, updatedAt: new Date().toISOString() };
    const list = state.interestedProperties.some((item) => item.id === interest.id)
      ? state.interestedProperties.map((item) => (item.id === interest.id ? nextInterest : item))
      : [nextInterest, ...state.interestedProperties];
    void persist(addAudit({ ...state, interestedProperties: list }, "update", "interested_property", interest.id, currentUser.id, undefined, nextInterest), "Interest saved");
  }

  function markInterested(leadId: string, propertyId: string) {
    const lead = state.leads.find((item) => item.id === leadId);
    if (!lead) return;
    const existing = state.interestedProperties.find((interest) => interest.leadId === leadId && interest.propertyId === propertyId);
    if (existing) {
      updateInterest({ ...existing, status: "Interested" });
      return;
    }
    const now = new Date().toISOString();
    const interest: InterestedProperty = {
      id: id("int"),
      leadId,
      propertyId,
      agentId: lead.agentId,
      teamId: lead.teamId,
      status: "Interested",
      note: "",
      createdAt: now,
      updatedAt: now
    };
    void persist(
      addAudit({ ...state, interestedProperties: [interest, ...state.interestedProperties] }, "create", "interested_property", interest.id, currentUser.id, undefined, interest),
      "Property marked interested"
    );
  }

  function removeInterest(interestId: string) {
    const previous = state.interestedProperties.find((interest) => interest.id === interestId);
    void persist(
      addAudit({ ...state, interestedProperties: state.interestedProperties.filter((interest) => interest.id !== interestId) }, "delete", "interested_property", interestId, currentUser.id, previous),
      "Interest removed"
    );
  }

  function nextViewingStart(leadId: string) {
    const taken = new Set(state.viewings.filter((viewing) => viewing.leadId === leadId).map((viewing) => viewing.startsAt));
    for (let day = 0; day < 14; day += 1) {
      for (const hour of [10, 13, 15, 17]) {
        const date = new Date();
        date.setDate(date.getDate() + day);
        date.setHours(hour, 0, 0, 0);
        const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        if (!taken.has(local)) return local;
      }
    }
    return `${todayIso()}T15:00`;
  }

  function createViewingForLeadProperty(leadId: string, propertyId: string, type = "Property viewing", startsAt = nextViewingStart(leadId)) {
    const lead = state.leads.find((item) => item.id === leadId);
    const property = state.properties.find((item) => item.id === propertyId);
    if (!lead || !property) return null;
    const activeViewings = state.viewings.filter((viewing) => viewing.leadId === leadId && !["Completed", "Cancelled", "No Show"].includes(viewing.status));
    if (activeViewings.length >= 3) {
      setStatus("This lead already has 3 active viewing appointments.");
      return null;
    }
    if (activeViewings.some((viewing) => viewing.startsAt === startsAt)) {
      setStatus("This lead already has a viewing at that date and time.");
      return null;
    }
    const viewing: Viewing = {
      id: id("view"),
      type,
      leadId,
      propertyId,
      agentId: lead.agentId,
      startsAt,
      location: property.location || "",
      status: "Scheduled",
      note: `Created from ${property.code || property.name}`.trim()
    };
    return viewing;
  }

  function createScheduleFromInterest(interest: InterestedProperty, type = "Property viewing") {
    const startsAt = nextViewingStart(interest.leadId);
    setScheduleDraft({ leadId: interest.leadId, propertyId: interest.propertyId, interestId: interest.id, type, date: datePart(startsAt), time: timePart(startsAt) });
  }

  function saveScheduleDraft(draft: ScheduleDraft) {
    const startsAt = combineDateTime(draft.date, draft.time);
    const viewing = createViewingForLeadProperty(draft.leadId, draft.propertyId, draft.type, startsAt);
    if (!viewing) return;
    const updatedInterest = draft.interestId
      ? state.interestedProperties.find((interest) => interest.id === draft.interestId)
      : undefined;
    const nextInterests = updatedInterest
      ? state.interestedProperties.map((item) => item.id === draft.interestId ? { ...updatedInterest, status: "Viewing scheduled", updatedAt: new Date().toISOString() } : item)
      : state.interestedProperties;
    void persist(
      addAudit(
        {
          ...state,
          viewings: [viewing, ...state.viewings],
          interestedProperties: nextInterests
        },
        "create",
        "viewing",
        viewing.id,
        currentUser.id,
        undefined,
        viewing
      ),
      "Schedule created"
    );
    setScheduleDraft(null);
  }

  function createScheduleFromMatch(leadId: string, propertyId: string) {
    const startsAt = nextViewingStart(leadId);
    setScheduleDraft({ leadId, propertyId, type: "Property viewing", date: datePart(startsAt), time: timePart(startsAt) });
  }

  function createFollowUp(leadId: string, propertyId?: string, requirementId?: string) {
    const lead = state.leads.find((item) => item.id === leadId);
    if (!lead) return;
    const existing = requirementId ? state.activities.find((item) => item.leadId === leadId && item.requirementId === requirementId && item.status !== "Completed") : undefined;
    if (existing) {
      setStatus("Follow-up already exists for this requirement.");
      setView("followupDetail");
      return;
    }
    const activity: Activity = {
      id: id("act"),
      leadId,
      requirementId,
      propertyId,
      agentId: lead.agentId,
      type: "Call",
      dueAt: `${todayIso()}T17:00`,
      status: "Open",
      note: "Follow up on property interest."
    };
    void persist(addAudit({ ...state, activities: [activity, ...state.activities] }, "create", "activity", activity.id, currentUser.id, undefined, activity), "Follow-up created");
    setView("followupDetail");
  }

  function updateViewing(viewing: Viewing) {
    const nextViewing = { ...viewing };
    const duplicate = state.viewings.some((item) => item.id !== viewing.id && item.leadId === viewing.leadId && item.startsAt === viewing.startsAt && !["Completed", "Cancelled", "No Show"].includes(item.status));
    if (duplicate) {
      setStatus("This lead already has a viewing at that date and time.");
      return;
    }
    void persist(
      addAudit({ ...state, viewings: state.viewings.map((item) => (item.id === viewing.id ? nextViewing : item)) }, "update", "viewing", viewing.id, currentUser.id, undefined, nextViewing),
      "Schedule saved"
    );
  }

  function deleteViewing(viewingId: string) {
    const previous = state.viewings.find((viewing) => viewing.id === viewingId);
    void persist(addAudit({ ...state, viewings: state.viewings.filter((viewing) => viewing.id !== viewingId) }, "delete", "viewing", viewingId, currentUser.id, previous), "Schedule deleted");
  }

  function updateActivity(activity: Activity) {
    const completedAt = activity.status === "Completed" ? activity.completedAt || new Date().toISOString() : undefined;
    const nextActivity = { ...activity, completedAt };
    void persist(
      addAudit({ ...state, activities: state.activities.map((item) => (item.id === activity.id ? nextActivity : item)) }, "update", "activity", activity.id, currentUser.id, undefined, nextActivity),
      "Follow-up saved"
    );
  }

  function deleteActivity(activityId: string) {
    const previous = state.activities.find((activity) => activity.id === activityId);
    void persist(addAudit({ ...state, activities: state.activities.filter((activity) => activity.id !== activityId) }, "delete", "activity", activityId, currentUser.id, previous), "Follow-up deleted");
  }

  function updateDashboardLayout(nextLayout: DashboardLayout) {
    const savedLayout = { ...nextLayout, updatedAt: new Date().toISOString() };
    const list = state.dashboardLayouts.some((item) => item.id === savedLayout.id)
      ? state.dashboardLayouts.map((item) => (item.id === savedLayout.id ? savedLayout : item))
      : [...state.dashboardLayouts, savedLayout];
    void persist(addAudit({ ...state, dashboardLayouts: list }, "update", "dashboard_layout", savedLayout.id, currentUser.id, undefined, savedLayout), "Dashboard saved");
  }

  function showSidebar() {
    setSidebarCollapsed(false);
    setSidebarOpen(true);
  }

  function hideSidebar() {
    setSidebarCollapsed(true);
  }

  const activeProperties = useMemo(() => matchableProperties(visibleProperties), [visibleProperties]);
  const activeLeads = useMemo(() => matchableLeads(visibleLeads), [visibleLeads]);
  const activeRequirements = useMemo(() => {
    const activeLeadIds = new Set(activeLeads.map((lead) => lead.id));
    return scopedState.requirements.filter((requirement) => activeLeadIds.has(requirement.leadId));
  }, [activeLeads, scopedState.requirements]);

  const selectedMatches = useMemo(
    () => {
      const selectedLead = visibleLeads.find((lead) => lead.id === selectedRequirement?.leadId);
      if (selectedLead && isClosedLeadStatus(selectedLead.status)) return [];
      return rankProperties(selectedRequirement, activeProperties, 20).filter((property) => property.matchScore >= HIGH_MATCH_THRESHOLD);
    },
    [selectedRequirement, activeProperties, visibleLeads]
  );

  const highPotentialLeads = useMemo(() => {
    return activeLeads
      .map((lead) => {
        const requirements = state.requirements.filter((item) => item.leadId === lead.id);
        const best = requirements.reduce((score, requirement) => Math.max(score, rankProperties(requirement, activeProperties, 1)[0]?.matchScore || 0), 0);
        return { lead, score: best };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [activeLeads, activeProperties, state.requirements]);

  const propertyInterest = useMemo(() => {
    return activeProperties
      .map((property) => {
        const best = activeRequirements.reduce((score, requirement) => Math.max(score, rankProperties(requirement, [property], 1)[0]?.matchScore || 0), 0);
        const interestedCount = state.interestedProperties.filter((interest) => interest.propertyId === property.id).length;
        return { property, best, interestedCount };
      })
      .sort((a, b) => b.best - a.best);
  }, [activeProperties, activeRequirements, state.interestedProperties]);

  const conversion = visibleLeads.length ? (visibleLeads.filter((lead) => lead.status === "Closed Won").length / visibleLeads.length) * 100 : 0;
  const soldOutProperties = scopedState.properties.filter((property) => isSoldOutStatus(property.status));
  const revenue = scopedState.opportunities.filter((opp) => opp.stage === "Won").reduce((sum, opp) => sum + opp.value, 0) + soldOutProperties.reduce((sum, property) => sum + property.price, 0);
  const commission = scopedState.commissions.reduce((sum, item) => sum + item.commissionAmount, 0) + soldOutProperties.reduce((sum, property) => sum + property.commissionAmount, 0);
  const intelligentAction = nextAction(scopedState, scopedState.requirements, visibleProperties);
  const visibleNav = nav.filter((item) => item.roles.includes(role));

  if (publicToken) {
    const property = state.properties.find((item) => item.publicToken === publicToken && item.publicVisible);
    const interestCount = property ? state.interestedProperties.filter((interest) => interest.propertyId === property.id).length : 0;
    return <PublicPropertyPage property={property} interestCount={interestCount} status={status} />;
  }

  if (authMessage && isSupabaseConfigured() && runtime.requireAuth) {
    return <AuthScreen message={authMessage} status={status} onGoogleLogin={handleGoogleLogin} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className={cn("fixed inset-y-0 left-0 z-40 w-72 border-r border-[#ead8b6]/80 bg-[#f5eadb] text-[#102421] shadow-[18px_0_44px_rgba(74,53,16,0.10)] transition-transform duration-300", sidebarOpen ? "translate-x-0" : "-translate-x-full", sidebarCollapsed ? "lg:-translate-x-full" : "lg:translate-x-0")}>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-5 py-4">
            <button className="hidden rounded-md bg-white/80 p-2 text-[#46635c] shadow-sm transition hover:bg-white hover:text-[#102421] lg:inline-grid" onClick={hideSidebar} aria-label="Hide sidebar" title="Hide sidebar"><PanelLeftClose size={18} /></button>
            <button className="ml-auto rounded-md bg-white/80 p-2 text-[#46635c] shadow-sm transition hover:bg-white hover:text-[#102421] lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close menu"><X size={20} /></button>
          </div>

          <div className="mx-4 mb-5 rounded-md bg-white/80 p-3 shadow-sm ring-1 ring-[#ead8b6]/70">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-[#d7b46a]/30 text-[#6f5116]"><UserRound size={20} /></div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{currentUser.name}</p>
                <p className="text-xs text-muted-foreground">{role} / {currentUser.teamId}</p>
                <p className="truncate text-xs text-muted-foreground">{currentUser.email}</p>
              </div>
            </div>
          </div>

          <nav className="grid gap-1 px-3">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={cn("flex min-h-11 items-center gap-3 rounded-md px-3 text-left text-sm font-medium text-[#46635c] transition hover:bg-white/70 hover:text-[#102421]", view === item.id && "bg-[#d7b46a]/30 text-[#102421] shadow-sm ring-1 ring-[#d7b46a]/40")}
                  onClick={() => { setView(item.id); setSidebarOpen(false); }}
                >
                  <Icon size={18} />
                  <FormattedMessage id={item.label} />
                </button>
              );
            })}
          </nav>

          <div className="mt-auto grid gap-3 px-5 py-5">
            <button className="btn secondary w-full bg-white/80" onClick={() => void handleLogout()}><LogOut size={16} /><FormattedMessage id="signOut" /></button>
          </div>
        </div>
      </aside>

      <div className={cn("min-w-0 transition-[padding] duration-300", sidebarCollapsed ? "lg:pl-0" : "lg:pl-72")}>
        <header className="sticky top-0 z-30 bg-background/90 px-4 py-3 backdrop-blur md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button className={cn("rounded-md bg-card p-2 shadow-sm", !sidebarCollapsed && "lg:hidden")} onClick={showSidebar} aria-label={sidebarCollapsed ? "Show sidebar" : "Open menu"} title={sidebarCollapsed ? "Show sidebar" : "Open menu"}>{sidebarCollapsed ? <PanelLeftOpen size={18} /> : <Menu size={18} />}</button>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{new Intl.DateTimeFormat("en", { weekday: "long", day: "numeric", month: "long" }).format(new Date())}</p>
                <h1 className="text-xl font-semibold md:text-2xl">{viewTitles[view] || intl.formatMessage({ id: "dashboard" })}</h1>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <SaveStatusBadge status={status} />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-[#d7b46a]/25 px-4 py-3 text-[#4b3510] shadow-[0_14px_32px_rgba(181,138,52,0.16)]">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-[#b58a34] text-white"><Sparkles size={18} /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em]">Priority now</p>
              <p className="text-sm font-semibold md:text-base">{intelligentAction}</p>
            </div>
          </div>
        </header>

        <main className="mx-auto min-w-0 max-w-7xl px-4 py-6 md:px-8">
          {view === "dashboard" && (
            <Dashboard
              state={scopedState}
              layout={layout}
              conversion={conversion}
              revenue={revenue}
              commission={commission}
              highPotentialLeads={highPotentialLeads}
              propertyInterest={propertyInterest.slice(0, 5)}
              updateLayout={updateDashboardLayout}
              openView={setView}
              openLead={(leadId) => { setSelectedLeadId(leadId); setView("leadDetail"); }}
              openProperty={(propertyId) => { setSelectedPropertyId(propertyId); setView("propertyDetail"); }}
            />
          )}
          {view === "leads" && (
            <LeadList
              state={state}
              scopedState={scopedState}
              query={query}
              setQuery={setQuery}
              setSelectedLeadId={setSelectedLeadId}
              openDetail={() => setView("leadDetail")}
              addLead={addLead}
            />
          )}
          {view === "leadDetail" && (
            <LeadDetail
              state={state}
              scopedState={scopedState}
              currentUser={effectiveUser}
              selectedLead={selectedLead}
              updateLead={updateLead}
              deleteLead={deleteLead}
              updateRequirement={updateRequirement}
              markInterested={markInterested}
              updateInterest={updateInterest}
              removeInterest={removeInterest}
              createSchedule={createScheduleFromInterest}
              createScheduleForMatch={createScheduleFromMatch}
              createFollowUp={createFollowUp}
              updateViewing={updateViewing}
              deleteViewing={deleteViewing}
              updateActivity={updateActivity}
              deleteActivity={deleteActivity}
              back={() => setView("leads")}
            />
          )}
          {view === "properties" && (
            <PropertyList
              state={state}
              scopedState={scopedState}
              query={query}
              setQuery={setQuery}
              setSelectedPropertyId={setSelectedPropertyId}
              openDetail={() => setView("propertyDetail")}
              addProperty={addProperty}
              interest={propertyInterest}
            />
          )}
          {view === "propertyDetail" && (
            <PropertyDetail
              state={state}
              scopedState={scopedState}
              currentUser={effectiveUser}
              selectedProperty={selectedProperty}
              updateProperty={updateProperty}
              deleteProperty={deleteProperty}
              interest={propertyInterest}
              markInterested={markInterested}
              createSchedule={createScheduleFromInterest}
              updateViewing={updateViewing}
              deleteViewing={deleteViewing}
              back={() => setView("properties")}
            />
          )}
          {view === "scheduleDetail" && <ScheduleList state={scopedState} masterData={state.masterData} updateViewing={updateViewing} deleteViewing={deleteViewing} />}
          {view === "followupDetail" && <FollowUpList state={scopedState} masterData={state.masterData} updateActivity={updateActivity} deleteActivity={deleteActivity} />}
          {view === "configuration" && role === "Admin" && <Configuration state={state} persist={persist} currentUser={currentUser} />}
        </main>
      </div>
      {scheduleDraft ? <ScheduleDialog draft={scheduleDraft} state={state} masterData={state.masterData} setDraft={setScheduleDraft} onSave={saveScheduleDraft} onClose={() => setScheduleDraft(null)} /> : null}
    </div>
  );
}

function AuthScreen({ message, status, onGoogleLogin }: { message: string; status: SaveStatus; onGoogleLogin: () => void }) {
  const homeMessage = message.startsWith("Please sign in with Google")
    ? "Welcome back. Continue with Google to open your workspace."
    : message;
  return (
    <div className="grid min-h-screen place-items-center bg-[#f5eadb] px-4">
      <section className="w-full max-w-lg rounded-lg bg-white/80 p-7 text-center shadow-[0_24px_80px_rgba(44,26,10,0.12)] backdrop-blur md:p-9">
        <img src="/home-lovin-logo.png" alt="Home Lovin" className="mx-auto mb-5 h-40 w-40 rounded-md object-contain md:h-48 md:w-48" />
        <h1 className="text-2xl font-semibold text-[#3b2517]">Home Lovin CRM</h1>
        <p className="mt-2 text-sm text-muted-foreground">Love where you live</p>
        <div className="my-6 rounded-md bg-[#d7b46a]/12 px-4 py-3 text-sm text-[#6a4a10]">{homeMessage}</div>
        <button className="btn google w-full bg-white text-[#1f1f1f] shadow-[0_12px_30px_rgba(44,26,10,0.12)] hover:bg-white" onClick={onGoogleLogin}><GoogleLogo />Continue with Google</button>
        <p className="mt-4 text-center text-xs text-muted-foreground">{String(status)}</p>
      </section>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function ScheduleDialog({ draft, state, masterData, setDraft, onSave, onClose }: { draft: ScheduleDraft; state: CrmState; masterData: MasterData; setDraft: (draft: ScheduleDraft) => void; onSave: (draft: ScheduleDraft) => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#102421]/45 px-4 backdrop-blur-sm">
      <section className="w-full max-w-2xl rounded-lg bg-card p-5 shadow-[0_24px_80px_rgba(16,36,33,0.26)]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Schedule viewing</h2>
            <p className="text-sm text-muted-foreground">Select the lead, property, date, and time before saving.</p>
          </div>
          <IconButton label="Close schedule" onClick={onClose}><X size={16} /></IconButton>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <Select label="Lead" value={draft.leadId} onChange={(leadId) => setDraft({ ...draft, leadId })}>{state.leads.map((lead) => <option key={lead.id} value={lead.id}>{lead.name || lead.email || lead.id}</option>)}</Select>
          <Select label="Property" value={draft.propertyId} onChange={(propertyId) => setDraft({ ...draft, propertyId })}>{state.properties.map((property) => <option key={property.id} value={property.id}>{property.name || property.code}</option>)}</Select>
          <Input label="Date" type="date" value={draft.date} onChange={(date) => setDraft({ ...draft, date })} />
          <Input label="Time" type="time" value={draft.time} onChange={(time) => setDraft({ ...draft, time })} />
          <Select label="Type" value={draft.type} onChange={(type) => setDraft({ ...draft, type })}>{(masterData.scheduleTypes || ["Property viewing"]).map((value) => <option key={value}>{value}</option>)}</Select>
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => onSave(draft)}><CalendarPlus size={16} />Save schedule</button>
        </div>
      </section>
    </div>
  );
}

function PublicPropertyPage({ property, interestCount, status }: { property?: Property; interestCount: number; status: SaveStatus }) {
  if (!property) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <section className="max-w-md rounded-lg bg-card p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto mb-3 text-[#b58a34]" />
          <h1 className="text-xl font-semibold">Property link unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">{String(status)}</p>
        </section>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto grid max-w-5xl gap-6 px-4 py-8 md:px-8">
        <section className="overflow-hidden rounded-lg bg-card shadow-sm">
          <div className="grid min-h-72 place-items-center bg-[#102421] text-center text-white">
            {property.photos ? <img src={property.photos} alt={property.name} className="h-full max-h-[420px] w-full object-cover" /> : <Building2 size={72} className="text-[#d7b46a]" />}
          </div>
          <div className="grid gap-5 p-6 md:grid-cols-[1fr_auto]">
            <div>
              <p className="text-sm font-semibold text-[#b58a34]">{property.code}</p>
              <h1 className="mt-1 text-3xl font-semibold">{property.name || property.development}</h1>
              <p className="mt-2 text-muted-foreground">{property.location} / {property.type} / {property.purpose}</p>
            </div>
            <div className="text-left md:text-right">
              <p className="text-2xl font-semibold">{money(property.promotionPrice || property.price)}</p>
              <p className="text-sm text-muted-foreground">{property.status} / {numberText(interestCount)} interested lead{interestCount === 1 ? "" : "s"}</p>
            </div>
          </div>
        </section>
        <section className="grid gap-4 md:grid-cols-3">
          <Stat label="Rooms" value={`${numberText(property.bedrooms)} bed / ${numberText(property.bathrooms)} bath`} hint={`${numberText(property.size || 0)} sqm`} />
          <Stat label="Transport" value={property.nearBts || property.nearMrt || "Available on request"} hint={property.location} />
          <Stat label="Features" value={[property.pool && "Pool", property.fitness && "Fitness", property.garden && "Garden"].filter(Boolean).join(", ") || "Core facilities"} hint={property.facilities} />
        </section>
      </main>
    </div>
  );
}

function SaveStatusBadge({ status }: { status: SaveStatus }) {
  const text = status === "Loading" ? "Loading data..." : status === "Saving" ? "Saving..." : status === "Error" ? "Save failed. Please retry." : String(status || "All changes saved");
  const Icon = status === "Loading" || status === "Saving" ? Loader2 : status === "Error" || String(status).toLowerCase().includes("failed") ? AlertTriangle : CheckCircle2;
  return <span className="inline-flex min-h-10 items-center gap-2 rounded-md bg-card px-3 py-2 text-xs font-semibold text-muted-foreground shadow-sm"><Icon size={15} className={cn((status === "Loading" || status === "Saving") && "animate-spin")} />{text}</span>;
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return <div className="rounded-lg bg-card p-5 shadow-sm ring-1 ring-[#ead8b6]/60"><p className="text-sm text-muted-foreground">{label}</p><p className="mt-2 text-2xl font-semibold">{value}</p><p className="mt-2 text-xs text-muted-foreground">{hint}</p></div>;
}

function Dashboard(props: {
  state: CrmState;
  layout: DashboardLayout;
  conversion: number;
  revenue: number;
  commission: number;
  highPotentialLeads: { lead: Lead; score: number }[];
  propertyInterest: { property: Property; best: number; interestedCount: number }[];
  updateLayout: (layout: DashboardLayout) => void;
  openView: (view: View) => void;
  openLead: (leadId: string) => void;
  openProperty: (propertyId: string) => void;
}) {
  const [dragged, setDragged] = useState<DashboardWidgetId | null>(null);
  const [expandedWidgets, setExpandedWidgets] = useState<DashboardWidgetId[]>([]);
  const visibleWidgets = props.layout.widgets.filter((widget) => !props.layout.hiddenWidgets.includes(widget));
  const hiddenWidgets = defaultDashboardWidgets.filter((widget) => props.layout.hiddenWidgets.includes(widget) || !props.layout.widgets.includes(widget));

  function reorder(target: DashboardWidgetId) {
    if (!dragged || dragged === target) return;
    const next = props.layout.widgets.filter((widget) => widget !== dragged);
    const targetIndex = next.indexOf(target);
    next.splice(targetIndex, 0, dragged);
    props.updateLayout({ ...props.layout, widgets: next });
    setDragged(null);
  }

  function hide(widget: DashboardWidgetId) {
    props.updateLayout({ ...props.layout, hiddenWidgets: [...new Set([...props.layout.hiddenWidgets, widget])] });
  }

  function toggleExpand(widget: DashboardWidgetId) {
    setExpandedWidgets((current) => current.includes(widget) ? current.filter((item) => item !== widget) : [...current, widget]);
  }

  function show(widget: DashboardWidgetId) {
    props.updateLayout({
      ...props.layout,
      widgets: props.layout.widgets.includes(widget) ? props.layout.widgets : [...props.layout.widgets, widget],
      hiddenWidgets: props.layout.hiddenWidgets.filter((item) => item !== widget)
    });
  }

  const widgetMap: Record<DashboardWidgetId, ReactNode> = {
    conversion: <Stat label="Conversion Rate" value={percent(props.conversion)} hint={`${numberText(props.state.leads.filter((lead) => lead.status === "Closed Won").length)} won from ${numberText(props.state.leads.length)} leads`} />,
    commission: <Stat label="Gained Commission" value={money(props.commission)} hint={`${money(props.revenue)} closed value`} />,
    topLeads: <Ranked rows={props.highPotentialLeads.slice(0, 3).map((item) => ({ title: item.lead.name || "Untitled lead", subtitle: `${item.lead.status} / ${money(item.lead.budget)}`, score: item.score, onOpen: () => props.openLead(item.lead.id) }))} />,
    topProperties: <Ranked rows={props.propertyInterest.slice(0, 3).map((item) => ({ title: item.property.name || item.property.code, subtitle: `${money(item.property.price)} / ${numberText(item.interestedCount)} interested`, score: item.best, onOpen: () => props.openProperty(item.property.id) }))} />,
    schedule: <SchedulePreview state={props.state} openProperty={props.openProperty} />,
    activity: <ActivityPreview state={props.state} openLead={props.openLead} />
  };

  return (
    <div className="grid gap-5">
      <Panel title="Home Lovin Assistant" icon={Sparkles} action={hiddenWidgets.length ? <Select label="Add widget" value="" onChange={(value) => value && show(value as DashboardWidgetId)}><option value="">Choose widget</option>{hiddenWidgets.map((widget) => <option key={widget} value={widget}>{widgetLabels[widget]}</option>)}</Select> : undefined}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <button className="rounded-md bg-background p-4 text-left shadow-sm transition hover:bg-white" onClick={() => props.openView("leads")}>
            <p className="text-sm text-muted-foreground">Total leads</p>
            <p className="mt-1 text-2xl font-semibold">{numberText(props.state.leads.length)}</p>
            <p className="mt-2 text-xs font-semibold text-[#7a5a14]">Open lead pipeline</p>
          </button>
          <button className="rounded-md bg-background p-4 text-left shadow-sm transition hover:bg-white" onClick={() => props.openView("properties")}>
            <p className="text-sm text-muted-foreground">Total properties</p>
            <p className="mt-1 text-2xl font-semibold">{numberText(props.state.properties.length)}</p>
            <p className="mt-2 text-xs font-semibold text-[#7a5a14]">Open property pipeline</p>
          </button>
          <button className="rounded-md bg-background p-4 text-left shadow-sm transition hover:bg-white" onClick={() => props.openView("followupDetail")}>
            <p className="text-sm text-muted-foreground">Total follow-ups</p>
            <p className="mt-1 text-2xl font-semibold">{numberText(props.state.activities.length)}</p>
            <p className="mt-2 text-xs font-semibold text-[#7a5a14]">Open follow-up detail</p>
          </button>
          <button className="rounded-md bg-background p-4 text-left shadow-sm transition hover:bg-white" onClick={() => props.openView("scheduleDetail")}>
            <p className="text-sm text-muted-foreground">Total scheduled</p>
            <p className="mt-1 text-2xl font-semibold">{numberText(props.state.viewings.filter((viewing) => viewing.status === "Scheduled").length)}</p>
            <p className="mt-2 text-xs font-semibold text-[#7a5a14]">Open schedule detail</p>
          </button>
        </div>
      </Panel>
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleWidgets.map((widget) => (
          <div
            key={widget}
            draggable
            onDragStart={() => setDragged(widget)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => reorder(widget)}
            className={cn("rounded-lg bg-card p-5 shadow-sm", expandedWidgets.includes(widget) && "md:col-span-2 xl:col-span-3")}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <GripVertical size={16} className="text-muted-foreground" />
                <h2 className="font-semibold">{widgetLabels[widget]}</h2>
              </div>
              <div className="flex items-center gap-1">
                <IconButton label={expandedWidgets.includes(widget) ? "Collapse widget" : "Expand widget"} onClick={() => toggleExpand(widget)}>{expandedWidgets.includes(widget) ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</IconButton>
                <IconButton label="Hide widget" onClick={() => hide(widget)}><X size={15} /></IconButton>
              </div>
            </div>
            {widgetMap[widget]}
          </div>
        ))}
      </section>
    </div>
  );
}

function Ranked({ rows }: { rows: { title: string; subtitle: string; score: number; onOpen?: () => void }[] }) {
  if (!rows.length) return <EmptyState text="No ranked records yet." />;
  return <div className="grid gap-3">{rows.map((row) => <button key={row.title} className="flex items-center justify-between rounded-md bg-background p-3 text-left shadow-sm transition hover:bg-white" onClick={row.onOpen}><div><p className="font-medium">{row.title}</p><p className="text-sm text-muted-foreground">{row.subtitle}</p></div><span className="rounded-md bg-[#d7b46a]/20 px-2 py-1 text-sm font-semibold text-[#7a5a14]">{numberText(row.score)}%</span></button>)}</div>;
}

function SchedulePreview({ state, openProperty }: { state: CrmState; openProperty: (propertyId: string) => void }) {
  const rows = state.viewings.slice(0, 4);
  if (!rows.length) return <EmptyState text="No upcoming schedule." />;
  return <div className="grid gap-2">{rows.map((item) => <button className="rounded-md bg-background p-3 text-left text-sm shadow-sm transition hover:bg-white" key={item.id} onClick={() => openProperty(item.propertyId)}><p className="font-medium">{dateTime(item.startsAt)} / {item.status}</p><p className="text-muted-foreground">{state.leads.find((lead) => lead.id === item.leadId)?.name || "Lead"} / {state.properties.find((property) => property.id === item.propertyId)?.name || "Property"}</p></button>)}</div>;
}

function ActivityPreview({ state, openLead }: { state: CrmState; openLead: (leadId: string) => void }) {
  const rows = state.activities.slice(0, 4);
  if (!rows.length) return <EmptyState text="No recent activity." />;
  return <div className="grid gap-2">{rows.map((item) => <button className="rounded-md bg-background p-3 text-left text-sm shadow-sm transition hover:bg-white" key={item.id} onClick={() => openLead(item.leadId)}><p className="font-medium">{item.type} / {item.status}</p><p className="text-muted-foreground">{dateTime(item.dueAt)} / {state.leads.find((lead) => lead.id === item.leadId)?.name || "Lead"}</p></button>)}</div>;
}

function LeadList(props: {
  state: CrmState;
  scopedState: CrmState;
  query: string;
  setQuery: (value: string) => void;
  setSelectedLeadId: (id: string) => void;
  openDetail: () => void;
  addLead: () => void;
}) {
  const [sort, setSort] = useState("updated");
  const [requirementField, setRequirementField] = useState<RequirementFilterKey>("purpose");
  const [requirementFilter, setRequirementFilter] = useState("");
  const statuses = props.state.masterData.leadStatuses || [];
  const leads = props.scopedState.leads
    .filter((lead) => {
      const requirements = props.state.requirements.filter((requirement) => requirement.leadId === lead.id);
      const searchText = `${Object.values(lead).join(" ")} ${requirements.map((requirement) => Object.values(requirement).join(" ")).join(" ")}`.toLowerCase();
      return searchText.includes(props.query.toLowerCase()) && requirementPassesFilter(requirements, requirementField, requirementFilter);
    })
    .sort((a, b) => {
      if (sort === "budget") return b.budget - a.budget;
      if (sort === "name") return a.name.localeCompare(b.name);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  function openLead(leadId: string) {
    props.setSelectedLeadId(leadId);
    props.openDetail();
  }

  return (
    <div className="grid gap-5">
      <Panel title="Lead Pipeline" icon={Users} action={<button className="btn add" onClick={props.addLead}><Plus size={16} /><FormattedMessage id="addLead" /></button>}>
        <div className="grid gap-3 lg:grid-cols-[1fr_220px_1fr_180px]">
          <Input placeholder="Search leads and requirements" value={props.query} onChange={props.setQuery} />
          <Select label="Requirement field" value={requirementField} onChange={(value) => setRequirementField(value as RequirementFilterKey)}>{requirementFilterFields.map((field) => <option key={field.key} value={field.key}>{field.label}</option>)}</Select>
          <Input label="Requirement filter" placeholder="Any value" value={requirementFilter} onChange={setRequirementFilter} />
          <Select label="Sort" value={sort} onChange={setSort}><option value="updated">Updated</option><option value="budget">Budget</option><option value="name">Name</option></Select>
        </div>
      </Panel>
      <Kanban columns={statuses} fallbackColumn="Unassigned">
        {(status) => leads.filter((lead) => (lead.status || "Unassigned") === status).map((lead) => {
          const requirements = props.state.requirements.filter((item) => item.leadId === lead.id);
          const matchCount = isClosedLeadStatus(lead.status) ? 0 : requirements.reduce((sum, requirement) => sum + rankProperties(requirement, matchableProperties(props.scopedState.properties), 20).filter((property) => property.matchScore >= HIGH_MATCH_THRESHOLD).length, 0);
          const interestCount = props.state.interestedProperties.filter((interest) => interest.leadId === lead.id).length;
          const followUpCount = props.state.activities.filter((activity) => activity.leadId === lead.id).length;
          return (
            <button className="list-row" key={lead.id} onClick={() => openLead(lead.id)}>
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-semibold">{lead.name || "Untitled lead"}</p>
                  <span className="rounded-md bg-muted px-2 py-1 text-xs font-semibold text-muted-foreground">{lead.temperature}</span>
                </div>
                <p className="text-sm text-muted-foreground">{lead.phone || lead.email || "No contact"} / {money(lead.budget)}</p>
                <p className="text-xs text-muted-foreground">{requirements[0]?.purpose || "Requirement"} / {requirements[0]?.propertyType || "Any type"} / {requirements[0]?.location || "Any area"}</p>
                <p className="text-xs text-muted-foreground">{numberText(matchCount)} matches / {numberText(interestCount)} interested / {numberText(followUpCount)} follow-ups</p>
              </div>
              <ChevronRight size={16} />
            </button>
          );
        })}
      </Kanban>
    </div>
  );
}

function LeadQuickSummary({ lead, requirements, followUps, viewings }: { lead: Lead; requirements: Requirement[]; followUps: Activity[]; viewings: Viewing[] }) {
  const nextViewing = [...viewings].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())[0];
  const nextFollowUp = [...followUps].filter((item) => item.status !== "Completed").sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime())[0];
  return (
    <section className="grid gap-3 rounded-lg bg-card p-4 shadow-sm md:grid-cols-5">
      <MiniFact label="Lead" value={lead.name || "Untitled"} hint={lead.phone || lead.email || "No contact"} />
      <MiniFact label="Status" value={lead.status} hint={lead.temperature} />
      <MiniFact label="Budget" value={money(lead.budget)} hint={`${numberText(requirements.length)} requirement${requirements.length === 1 ? "" : "s"}`} />
      <MiniFact label="Next viewing" value={nextViewing ? dateTime(nextViewing.startsAt) : "None"} hint={nextViewing?.status || "No schedule"} />
      <MiniFact label="Follow-up" value={nextFollowUp ? dateTime(nextFollowUp.dueAt) : "Clear"} hint={nextFollowUp?.status || "No open follow-up"} />
    </section>
  );
}

function PropertyQuickSummary({ property, score, interestedCount, viewings }: { property: Property; score: number; interestedCount: number; viewings: Viewing[] }) {
  const nextViewing = [...viewings].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())[0];
  return (
    <section className="grid gap-3 rounded-lg bg-card p-4 shadow-sm md:grid-cols-5">
      <MiniFact label="Price" value={money(property.price)} hint={property.type} />
      <MiniFact label="Location" value={property.location || "No location"} hint={property.nearBts || property.nearMrt || "Transit n/a"} />
      <MiniFact label="Rooms" value={`${numberText(property.bedrooms)} bed / ${numberText(property.bathrooms)} bath`} hint={`${numberText(property.size)} sqm`} />
      <MiniFact label="Lead fit" value={`${numberText(score)}%`} hint={`${numberText(interestedCount)} interested`} />
      <MiniFact label="Next viewing" value={nextViewing ? dateTime(nextViewing.startsAt) : "None"} hint={nextViewing?.status || "No schedule"} />
    </section>
  );
}

function MiniFact({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="min-w-0 rounded-md bg-background px-3 py-2">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className="truncate text-sm font-semibold">{value}</p>
      <p className="truncate text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function LeadDetail(props: {
  state: CrmState;
  scopedState: CrmState;
  currentUser: UserProfile;
  selectedLead?: Lead;
  updateLead: (lead: Lead) => void;
  deleteLead: (leadId: string) => void;
  updateRequirement: (requirement: Requirement) => void;
  markInterested: (leadId: string, propertyId: string) => void;
  updateInterest: (interest: InterestedProperty) => void;
  removeInterest: (interestId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
  createScheduleForMatch: (leadId: string, propertyId: string) => void;
  createFollowUp: (leadId: string, propertyId?: string, requirementId?: string) => void;
  updateViewing: (viewing: Viewing) => void;
  deleteViewing: (viewingId: string) => void;
  updateActivity: (activity: Activity) => void;
  deleteActivity: (activityId: string) => void;
  back: () => void;
}) {
  if (!props.selectedLead) return <EmptyState text="No lead selected." />;
  const isDraft = !props.state.leads.some((lead) => lead.id === props.selectedLead?.id);
  const requirements = props.state.requirements.filter((requirement) => requirement.leadId === props.selectedLead?.id);
  const interests = props.state.interestedProperties.filter((interest) => interest.leadId === props.selectedLead?.id);
  const followUps = props.state.activities.filter((activity) => activity.leadId === props.selectedLead?.id);
  const scheduled = props.state.viewings.filter((viewing) => viewing.leadId === props.selectedLead?.id);

  function addRequirement() {
    props.updateRequirement(emptyRequirement(props.selectedLead!.id, props.state.masterData, props.selectedLead));
  }

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button className="btn secondary w-fit" onClick={props.back}><ChevronRight className="rotate-180" size={16} />Back to lead pipeline</button>
        <button className="btn danger" onClick={() => props.deleteLead(props.selectedLead!.id)}><Trash2 size={16} />{isDraft ? "Cancel lead" : "Delete lead"}</button>
      </div>
      {isDraft ? <div className="rounded-lg bg-[#d7b46a]/15 p-4 text-sm font-semibold text-[#6a4a10] shadow-sm">Fill lead details and Save before this lead appears in the pipeline.</div> : <LeadQuickSummary lead={props.selectedLead} requirements={requirements} followUps={followUps} viewings={scheduled} />}
      {followUps.length ? <div className="rounded-md bg-[#d7b46a]/10 p-3 text-sm font-semibold text-[#7a5a14] shadow-sm">{numberText(followUps.length)} follow-up{followUps.length === 1 ? "" : "s"} on this lead</div> : null}
      <Panel title="Lead Profile" icon={Edit3}><LeadForm lead={props.selectedLead} masterData={props.state.masterData} users={props.state.users} allLeads={props.state.leads} currentUser={props.currentUser} canEdit={canEditLead(props.currentUser, props.selectedLead)} onSave={props.updateLead} /></Panel>
      {isDraft ? null : (
        <>
      <Panel title="Lead Requirements" icon={ClipboardList} action={<button className="btn add" onClick={addRequirement}><Plus size={16} />Add requirement</button>}>
        <div className="grid gap-5">
          {!requirements.length ? <EmptyState text="No requirements yet." /> : requirements.map((requirement, index) => {
            const matches = isClosedLeadStatus(props.selectedLead!.status) ? [] : rankProperties(requirement, matchableProperties(props.scopedState.properties), 20).filter((property) => property.matchScore >= HIGH_MATCH_THRESHOLD);
            const followUp = props.state.activities.find((activity) => activity.requirementId === requirement.id && activity.status !== "Completed");
            return (
              <div className="rounded-lg bg-background p-4 shadow-sm" key={requirement.id}>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-semibold">Requirement {numberText(index + 1)} / {requirement.purpose} / {requirement.propertyType}</h3>
                  <button className="btn secondary" onClick={() => props.createFollowUp(props.selectedLead!.id, undefined, requirement.id)}><ListChecks size={16} />{followUp ? "Open follow-up" : "Create follow-up"}</button>
                </div>
                {followUp ? <p className="mb-4 rounded-md bg-[#d7b46a]/10 p-3 text-sm text-[#7a5a14]">{followUp.type} due {dateTime(followUp.dueAt)} / {followUp.status}</p> : null}
                <RequirementForm requirement={requirement} masterData={props.state.masterData} onSave={props.updateRequirement} />
                <RequirementMatches lead={props.selectedLead!} requirement={requirement} matches={matches} interests={interests} schedules={scheduled} markInterested={props.markInterested} createSchedule={props.createScheduleForMatch} />
              </div>
            );
          })}
        </div>
      </Panel>
      <InterestedProperties state={props.state} lead={props.selectedLead} interests={interests} updateInterest={props.updateInterest} removeInterest={props.removeInterest} createSchedule={props.createSchedule} createFollowUp={props.createFollowUp} />
      <ScheduleList state={{ ...props.scopedState, viewings: scheduled }} masterData={props.state.masterData} updateViewing={props.updateViewing} deleteViewing={props.deleteViewing} title="Viewing Schedule" />
      <FollowUpList state={{ ...props.scopedState, activities: followUps }} masterData={props.state.masterData} updateActivity={props.updateActivity} deleteActivity={props.deleteActivity} title="Follow-ups Schedule" />
      <Timeline state={props.state} leadId={props.selectedLead.id} />
        </>
      )}
    </div>
  );
}

function RequirementMatches(props: {
  lead: Lead;
  requirement: Requirement;
  matches: PropertyMatch[];
  interests: InterestedProperty[];
  schedules: Viewing[];
  markInterested: (leadId: string, propertyId: string) => void;
  createSchedule: (leadId: string, propertyId: string) => void;
}) {
  return (
    <div className="mt-5 grid gap-3">
      <h4 className="text-sm font-semibold">Matching properties</h4>
      {!props.matches.length ? <EmptyState text="No high-confidence matches yet." /> : props.matches.map((property) => {
        const interested = props.interests.some((interest) => interest.propertyId === property.id);
        const viewing = props.schedules.find((schedule) => schedule.propertyId === property.id);
        return (
          <div className="rounded-md bg-card p-4 shadow-sm" key={`${props.requirement.id}-${property.id}`}>
            <div className="grid gap-4 md:grid-cols-[150px_1fr]">
              <RadarScore match={property} />
              <div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{property.name || property.code}</p>
                    <p className="text-sm text-muted-foreground">{property.location} / {property.type} / {money(property.price)}</p>
                  </div>
                </div>
                <MissingRequirements match={property} />
                {viewing ? <p className="mt-3 rounded-md bg-muted p-2 text-sm text-muted-foreground">Viewing: {dateTime(viewing.startsAt)} / {viewing.status}</p> : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="btn secondary" onClick={() => props.markInterested(props.lead.id, property.id)}><Sparkles size={16} />{interested ? "Interested" : "Mark interested"}</button>
                  <button className="btn secondary" onClick={() => props.createSchedule(props.lead.id, property.id)}><CalendarPlus size={16} />Schedule viewing</button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PropertyList(props: {
  state: CrmState;
  scopedState: CrmState;
  query: string;
  setQuery: (value: string) => void;
  setSelectedPropertyId: (id: string) => void;
  openDetail: () => void;
  addProperty: () => void;
  interest: { property: Property; best: number; interestedCount: number }[];
}) {
  const [sort, setSort] = useState("updated");
  const [requirementField, setRequirementField] = useState<RequirementFilterKey>("purpose");
  const [requirementFilter, setRequirementFilter] = useState("");
  const statuses = props.state.masterData.propertyStatuses || [];
  const activeLeadIds = useMemo(() => new Set(matchableLeads(props.scopedState.leads).map((lead) => lead.id)), [props.scopedState.leads]);
  const requirementsForMatching = useMemo(() => props.scopedState.requirements.filter((requirement) => activeLeadIds.has(requirement.leadId)), [activeLeadIds, props.scopedState.requirements]);

  function matchingLeadCount(property: Property) {
    if (isSoldOutStatus(property.status)) return 0;
    return new Set(
      requirementsForMatching
        .filter((requirement) => (rankProperties(requirement, [property], 1)[0]?.matchScore || 0) >= HIGH_MATCH_THRESHOLD)
        .map((requirement) => requirement.leadId)
    ).size;
  }

  const properties = props.scopedState.properties
    .filter((property) => {
      const matchedRequirements = isSoldOutStatus(property.status) ? [] : requirementsForMatching.filter((requirement) => (rankProperties(requirement, [property], 1)[0]?.matchScore || 0) >= HIGH_MATCH_THRESHOLD);
      const matchedRequirementText = matchedRequirements.map((requirement) => Object.values(requirement).join(" ")).join(" ");
      return `${Object.values(property).join(" ")} ${matchedRequirementText}`.toLowerCase().includes(props.query.toLowerCase())
        && requirementPassesFilter(matchedRequirements.length ? matchedRequirements : requirementsForMatching, requirementField, requirementFilter);
    })
    .sort((a, b) => {
      if (sort === "price") return b.price - a.price;
      if (sort === "group") return `${a.name}-${a.type}`.localeCompare(`${b.name}-${b.type}`);
      if (sort === "match") return (props.interest.find((item) => item.property.id === b.id)?.best || 0) - (props.interest.find((item) => item.property.id === a.id)?.best || 0);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  function openProperty(propertyId: string) {
    props.setSelectedPropertyId(propertyId);
    props.openDetail();
  }

  return (
    <div className="grid gap-5">
      <Panel title="Property Pipeline" icon={Building2} action={<button className="btn add" onClick={props.addProperty}><Plus size={16} /><FormattedMessage id="addProperty" /></button>}>
        <div className="grid gap-3 lg:grid-cols-[1fr_220px_1fr_180px]">
          <Input placeholder="Search properties and matched requirements" value={props.query} onChange={props.setQuery} />
          <Select label="Requirement field" value={requirementField} onChange={(value) => setRequirementField(value as RequirementFilterKey)}>{requirementFilterFields.map((field) => <option key={field.key} value={field.key}>{field.label}</option>)}</Select>
          <Input label="Requirement filter" placeholder="Any value" value={requirementFilter} onChange={setRequirementFilter} />
          <Select label="Sort" value={sort} onChange={setSort}><option value="updated">Updated</option><option value="match">Matching leads</option><option value="price">Price</option><option value="group">Name + type group</option></Select>
        </div>
      </Panel>
      <Kanban columns={statuses} fallbackColumn="Unassigned">
        {(status) => properties.filter((property) => (property.status || "Unassigned") === status).map((property) => {
          const match = props.interest.find((item) => item.property.id === property.id);
          const facilities = propertyFacilities(property);
          const views = propertyViewChoices(property);
          const leadCount = matchingLeadCount(property);
          const interestedCount = match?.interestedCount || 0;
          return (
            <button className="property-card" key={property.id} onClick={() => openProperty(property.id)}>
              <div className="property-card-media">
                {property.photos ? <img src={property.photos} alt={property.name || property.code} className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-[#b58a34]"><Building2 size={30} /></div>}
              </div>
              <div className="grid gap-3 p-3">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{property.name || property.development || property.code}</p>
                    <p className="mt-0.5 text-xs font-semibold text-[#7a5a14]">{property.code || "No property ID"}</p>
                  </div>
                  <span className="shrink-0 rounded-md bg-[#f1dfbd] px-2 py-1 text-xs font-semibold text-[#5d4210]">{property.status}</span>
                </div>
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <p className="text-lg font-semibold">{money(property.price)}</p>
                  <p className="text-xs text-muted-foreground">{property.type} / {property.location || "No location"}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="property-stat"><span>Lead matching</span><strong>{numberText(leadCount)}</strong></div>
                  <div className="property-stat"><span>Interested leads</span><strong>{numberText(interestedCount)}</strong></div>
                </div>
                <p className="text-xs text-muted-foreground">{numberText(property.bedrooms)} bed / {numberText(property.bathrooms)} bath / {numberText(property.size)} sqm / {property.nearBts || property.nearMrt || "Transit n/a"}</p>
                <div className="flex flex-wrap gap-1.5">
                  {views.map((view) => <span className="subtle-chip" key={`view-${property.id}-${view}`}>View: {view}</span>)}
                  {facilities.length ? facilities.map((facility) => <span className="soft-chip" key={`${property.id}-${facility}`}>{facility}</span>) : <span className="subtle-chip">No facilities listed</span>}
                </div>
              </div>
            </button>
          );
        })}
      </Kanban>
    </div>
  );
}

function PropertyDetail(props: {
  state: CrmState;
  scopedState: CrmState;
  currentUser: UserProfile;
  selectedProperty?: Property;
  updateProperty: (property: Property) => void;
  deleteProperty: (propertyId: string) => void;
  interest: { property: Property; best: number; interestedCount: number }[];
  markInterested: (leadId: string, propertyId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
  updateViewing: (viewing: Viewing) => void;
  deleteViewing: (viewingId: string) => void;
  back: () => void;
}) {
  if (!props.selectedProperty) return <EmptyState text="No property selected." />;
  const isDraft = !props.state.properties.some((property) => property.id === props.selectedProperty?.id);
  const selectedScore = props.interest.find((item) => item.property.id === props.selectedProperty?.id)?.best || 0;
  const visibleLeadIds = new Set(props.scopedState.leads.map((lead) => lead.id));
  const interested = props.state.interestedProperties.filter((interest) => interest.propertyId === props.selectedProperty?.id);
  const visibleInterested = interested.filter((interest) => visibleLeadIds.has(interest.leadId));
  const propertyViewings = props.state.viewings.filter((viewing) => viewing.propertyId === props.selectedProperty?.id);
  const suggestedLeads = props.scopedState.requirements
    .map((requirement) => {
      const lead = props.scopedState.leads.find((item) => item.id === requirement.leadId);
      if (!lead || isClosedLeadStatus(lead.status) || isSoldOutStatus(props.selectedProperty!.status)) return null;
      const match = rankProperties(requirement, [props.selectedProperty as Property], 1)[0];
      return match ? { lead, requirement, match, score: match.matchScore } : null;
    })
    .filter((item): item is { lead: Lead; requirement: Requirement; match: PropertyMatch; score: number } => Boolean(item && item.score >= HIGH_MATCH_THRESHOLD))
    .sort((a, b) => b.score - a.score);

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button className="btn secondary w-fit" onClick={props.back}><ChevronRight className="rotate-180" size={16} />Back to property pipeline</button>
        <button className="btn danger" onClick={() => props.deleteProperty(props.selectedProperty!.id)}><Trash2 size={16} />{isDraft ? "Cancel property" : "Delete property"}</button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-primary p-4 text-primary-foreground shadow-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em]">Property status</p>
          <p className="text-xl font-semibold">{isDraft ? "Draft detail" : props.selectedProperty.status}</p>
        </div>
        <p className="text-sm opacity-85">{props.selectedProperty.name || "Fill property detail before adding to pipeline"}</p>
      </div>
      {isDraft ? <div className="rounded-lg bg-[#d7b46a]/15 p-4 text-sm font-semibold text-[#6a4a10] shadow-sm">Fill property details and Save before this property appears in the pipeline.</div> : <PropertyQuickSummary property={props.selectedProperty} score={selectedScore} interestedCount={interested.length} viewings={propertyViewings} />}
      <Panel title={`Property Detail / Lead Fit ${numberText(selectedScore)}%`} icon={Building2}><PropertyForm property={props.selectedProperty} masterData={props.state.masterData} allProperties={props.state.properties} users={props.state.users} currentUser={props.currentUser} onSave={props.updateProperty} /></Panel>
      {isDraft ? null : (
        <>
      <PropertyLeadDetailPanel state={props.state} visibleInterested={visibleInterested} suggestedLeads={suggestedLeads} property={props.selectedProperty} markInterested={props.markInterested} createSchedule={props.createSchedule} />
      <ScheduleList state={{ ...props.scopedState, viewings: propertyViewings }} masterData={props.state.masterData} updateViewing={props.updateViewing} deleteViewing={props.deleteViewing} title="Viewing Schedule" />
      <Timeline state={props.state} propertyId={props.selectedProperty.id} />
        </>
      )}
    </div>
  );
}

function PropertyLeadDetailPanel(props: {
  state: CrmState;
  visibleInterested: InterestedProperty[];
  suggestedLeads: { lead: Lead; requirement: Requirement; match: PropertyMatch; score: number }[];
  property: Property;
  markInterested: (leadId: string, propertyId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
}) {
  const totalInterested = props.state.interestedProperties.filter((interest) => interest.propertyId === props.property.id).length;
  return (
    <Panel title={`Interested and Suggested Leads / Total ${numberText(totalInterested)}`} icon={Users}>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-3">
          <h3 className="text-sm font-semibold">Interested leads</h3>
          {!props.visibleInterested.length ? <EmptyState text="No accessible interested leads." /> : props.visibleInterested.map((interest) => {
            const lead = props.state.leads.find((item) => item.id === interest.leadId);
            const viewing = props.state.viewings.find((item) => item.leadId === interest.leadId && item.propertyId === props.property.id);
            return <div className="rounded-md bg-background p-3 text-sm" key={interest.id}><p className="font-medium">{lead?.name || "Restricted lead"} / {interest.status}</p><p className="text-muted-foreground">{interest.note || "No note"}</p>{viewing ? <p className="mt-2 rounded-md bg-muted p-2 text-muted-foreground">Viewing: {dateTime(viewing.startsAt)} / {viewing.status}</p> : null}<button className="btn secondary mt-3" onClick={() => props.createSchedule(interest)}><CalendarPlus size={16} />Schedule</button></div>;
          })}
        </div>
        <div className="grid content-start gap-3">
          <h3 className="text-sm font-semibold">Suggested leads</h3>
          {!props.suggestedLeads.length ? <EmptyState text="No suggested leads yet." /> : props.suggestedLeads.map((item) => {
            const viewing = props.state.viewings.find((schedule) => schedule.leadId === item.lead.id && schedule.propertyId === props.property.id);
            return (
              <div className="rounded-md bg-background p-3 text-sm" key={`${item.lead.id}-${item.requirement.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div><p className="font-medium">{item.lead.name}</p><p className="text-muted-foreground">{item.requirement.purpose} / {item.requirement.propertyType} / {money(item.lead.budget)}</p></div>
                  <span className="font-semibold">{numberText(item.score)}%</span>
                </div>
                <MissingRequirements match={item.match} />
                {viewing ? <p className="mt-2 rounded-md bg-muted p-2 text-muted-foreground">Viewing: {dateTime(viewing.startsAt)} / {viewing.status}</p> : null}
                <button className="btn secondary mt-3" onClick={() => props.markInterested(item.lead.id, props.property.id)}><Plus size={15} />Mark interested</button>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

function Leads(props: {
  state: CrmState;
  scopedState: CrmState;
  currentUser: UserProfile;
  query: string;
  setQuery: (value: string) => void;
  selectedLead?: Lead;
  selectedRequirement?: Requirement;
  selectedMatches: PropertyMatch[];
  setSelectedLeadId: (id: string) => void;
  addLead: () => void;
  updateLead: (lead: Lead) => void;
  updateRequirement: (requirement: Requirement) => void;
  markInterested: (leadId: string, propertyId: string) => void;
  updateInterest: (interest: InterestedProperty) => void;
  removeInterest: (interestId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
  createFollowUp: (leadId: string, propertyId?: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState("All");
  const [sort, setSort] = useState("updated");
  const leads = props.scopedState.leads
    .filter((lead) => statusFilter === "All" || lead.status === statusFilter)
    .filter((lead) => `${lead.name} ${lead.phone} ${lead.email} ${lead.nationality}`.toLowerCase().includes(props.query.toLowerCase()))
    .sort((a, b) => {
      if (sort === "budget") return b.budget - a.budget;
      if (sort === "name") return a.name.localeCompare(b.name);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  if (!props.selectedLead) {
    return <Panel title="Leads" icon={Users} action={<button className="btn add" onClick={props.addLead}><Plus size={16} /><FormattedMessage id="addLead" /></button>}><EmptyState text="No leads available in your access scope." /></Panel>;
  }

  const req = props.selectedRequirement || emptyRequirement(props.selectedLead.id, props.state.masterData, props.selectedLead);
  const interests = props.state.interestedProperties.filter((interest) => interest.leadId === props.selectedLead?.id);

  return (
    <SplitList
      title="Leads"
      action={<button className="btn add" onClick={props.addLead}><Plus size={16} /><FormattedMessage id="addLead" /></button>}
      query={props.query}
      setQuery={props.setQuery}
      filters={<div className="grid gap-2 md:grid-cols-2"><Select label="Status" value={statusFilter} onChange={setStatusFilter}><option>All</option>{(props.state.masterData.leadStatuses || []).map((value) => <option key={value}>{value}</option>)}</Select><Select label="Sort" value={sort} onChange={setSort}><option value="updated">Updated</option><option value="budget">Budget</option><option value="name">Name</option></Select></div>}
    >
      <div className="grid gap-3">
        {leads.map((lead) => {
          const requirement = props.state.requirements.find((item) => item.leadId === lead.id);
          const matchCount = isClosedLeadStatus(lead.status) ? 0 : rankProperties(requirement, matchableProperties(props.scopedState.properties), 10).filter((property) => property.matchScore >= HIGH_MATCH_THRESHOLD).length;
          const interestCount = props.state.interestedProperties.filter((interest) => interest.leadId === lead.id).length;
          const followUp = props.state.activities.find((activity) => activity.leadId === lead.id && activity.status !== "Completed");
          const agent = props.state.users.find((user) => user.id === lead.agentId);
          return (
            <button key={lead.id} className={cn("list-row", props.selectedLead?.id === lead.id && "active")} onClick={() => props.setSelectedLeadId(lead.id)}>
              <div className="min-w-0">
                <p className="truncate font-medium">{lead.name || "Untitled lead"}</p>
                <p className="text-sm text-muted-foreground">{lead.phone || "No phone"} / {money(lead.budget)} / {lead.status}</p>
                <p className="text-xs text-muted-foreground">{numberText(matchCount)} matches / {numberText(interestCount)} interested / {followUp ? followUp.status : "No follow-up"} / {agent?.name || "Unassigned"}</p>
              </div>
              <ChevronRight size={16} />
            </button>
          );
        })}
      </div>
      <div className="grid gap-6">
        <Panel title="Lead Profile" icon={Edit3}><LeadForm lead={props.selectedLead} masterData={props.state.masterData} users={props.state.users} allLeads={props.state.leads} currentUser={props.currentUser} canEdit={canEditLead(props.currentUser, props.selectedLead)} onSave={props.updateLead} /></Panel>
        <Panel title="Lead Requirement" icon={ClipboardList}><RequirementForm requirement={req} masterData={props.state.masterData} onSave={props.updateRequirement} /></Panel>
        <LeadMatches lead={props.selectedLead} matches={props.selectedMatches} interests={interests} markInterested={props.markInterested} />
        <InterestedProperties
          state={props.state}
          lead={props.selectedLead}
          interests={interests}
          updateInterest={props.updateInterest}
          removeInterest={props.removeInterest}
          createSchedule={props.createSchedule}
          createFollowUp={props.createFollowUp}
        />
        <Timeline state={props.state} leadId={props.selectedLead.id} />
      </div>
    </SplitList>
  );
}

function LeadMatches({ lead, matches, interests, markInterested }: { lead: Lead; matches: PropertyMatch[]; interests: InterestedProperty[]; markInterested: (leadId: string, propertyId: string) => void }) {
  return (
    <Panel title="Matching Properties" icon={Sparkles}>
      <div className="grid gap-3">
        {!matches.length ? <EmptyState text="No high-match properties yet." /> : matches.map((property) => {
          const interested = interests.some((interest) => interest.propertyId === property.id);
          return (
            <div className="rounded-md bg-background p-4" key={property.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{property.name || property.code}</p>
                  <p className="text-sm text-muted-foreground">{property.location} / {property.type} / {money(property.price)}</p>
                </div>
                <span className="rounded-md bg-primary px-3 py-1 text-sm font-semibold text-primary-foreground">{numberText(property.matchScore)}%</span>
              </div>
              <MatchBreakdown match={property} />
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="btn secondary" onClick={() => markInterested(lead.id, property.id)}><Sparkles size={16} />{interested ? "Update interest" : "Mark interested"}</button>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function InterestedProperties(props: {
  state: CrmState;
  lead: Lead;
  interests: InterestedProperty[];
  updateInterest: (interest: InterestedProperty) => void;
  removeInterest: (interestId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
  createFollowUp: (leadId: string, propertyId?: string) => void;
}) {
  return (
    <Panel title="Interested Properties" icon={Bell}>
      <div className="grid gap-3">
        {!props.interests.length ? <EmptyState text="No interested properties marked yet." /> : props.interests.map((interest) => {
          const property = props.state.properties.find((item) => item.id === interest.propertyId);
          return (
            <div className="grid gap-3 rounded-md bg-background p-4" key={interest.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{property?.name || "Property"}</p>
                  <p className="text-sm text-muted-foreground">{property?.code} / {property ? money(property.price) : ""}</p>
                </div>
                <Select label="Status" value={interest.status} onChange={(status) => props.updateInterest({ ...interest, status })}>
                  {(props.state.masterData.interestStatuses || []).map((value) => <option key={value}>{value}</option>)}
                </Select>
              </div>
              <TextArea label="Interest note" value={interest.note} onChange={(note) => props.updateInterest({ ...interest, note })} />
              <div className="flex flex-wrap gap-2">
                <button className="btn secondary" onClick={() => props.createSchedule(interest)}><CalendarPlus size={16} />Schedule</button>
                <button className="btn secondary" onClick={() => props.createFollowUp(props.lead.id, interest.propertyId)}><ListChecks size={16} />Follow up</button>
                <button className="btn danger" onClick={() => props.removeInterest(interest.id)}><Trash2 size={16} />Remove</button>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function Properties(props: {
  state: CrmState;
  scopedState: CrmState;
  currentUser: UserProfile;
  query: string;
  setQuery: (value: string) => void;
  selectedProperty?: Property;
  setSelectedPropertyId: (id: string) => void;
  addProperty: () => void;
  updateProperty: (property: Property) => void;
  interest: { property: Property; best: number; interestedCount: number }[];
  markInterested: (leadId: string, propertyId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState("All");
  const [sort, setSort] = useState("updated");
  const properties = props.scopedState.properties
    .filter((property) => statusFilter === "All" || property.status === statusFilter)
    .filter((property) => `${property.name} ${property.location} ${property.code} ${property.development}`.toLowerCase().includes(props.query.toLowerCase()))
    .sort((a, b) => {
      if (sort === "price") return b.price - a.price;
      if (sort === "match") return (props.interest.find((item) => item.property.id === b.id)?.best || 0) - (props.interest.find((item) => item.property.id === a.id)?.best || 0);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  if (!props.selectedProperty) {
    return <Panel title="Properties" icon={Building2} action={<button className="btn add" onClick={props.addProperty}><Plus size={16} /><FormattedMessage id="addProperty" /></button>}><EmptyState text="No properties available." /></Panel>;
  }

  const selectedScore = props.interest.find((item) => item.property.id === props.selectedProperty?.id)?.best || 0;
  const interested = props.state.interestedProperties.filter((interest) => interest.propertyId === props.selectedProperty?.id);
  const visibleLeadIds = new Set(props.scopedState.leads.map((lead) => lead.id));
  const visibleInterested = interested.filter((interest) => visibleLeadIds.has(interest.leadId));
  const suggestedLeads = props.scopedState.leads
    .map((lead) => {
      if (isClosedLeadStatus(lead.status) || isSoldOutStatus(props.selectedProperty!.status)) return { lead, score: 0 };
      const requirement = props.state.requirements.find((item) => item.leadId === lead.id);
      const match = rankProperties(requirement, [props.selectedProperty as Property], 1)[0];
      return { lead, score: match?.matchScore || 0 };
    })
    .filter((item) => item.score >= HIGH_MATCH_THRESHOLD)
    .sort((a, b) => b.score - a.score);

  return (
    <SplitList
      title="Properties"
      action={<button className="btn add" onClick={props.addProperty}><Plus size={16} /><FormattedMessage id="addProperty" /></button>}
      query={props.query}
      setQuery={props.setQuery}
      filters={<div className="grid gap-2 md:grid-cols-2"><Select label="Status" value={statusFilter} onChange={setStatusFilter}><option>All</option>{(props.state.masterData.propertyStatuses || []).map((value) => <option key={value}>{value}</option>)}</Select><Select label="Sort" value={sort} onChange={setSort}><option value="updated">Updated</option><option value="match">Matching leads</option><option value="price">Price</option></Select></div>}
    >
      <div className="grid gap-3">
        {properties.map((property) => {
          const match = props.interest.find((item) => item.property.id === property.id);
          return (
            <button key={property.id} className={cn("list-row", props.selectedProperty?.id === property.id && "active")} onClick={() => props.setSelectedPropertyId(property.id)}>
              <div className="min-w-0">
                <p className="truncate font-medium">{property.name || property.code}</p>
                <p className="text-sm text-muted-foreground">{property.code} / {money(property.price)} / {property.status}</p>
                <p className="text-xs text-muted-foreground">{numberText(match?.best || 0)}% lead fit / {numberText(match?.interestedCount || 0)} interested leads</p>
              </div>
              <ChevronRight size={16} />
            </button>
          );
        })}
      </div>
      <div className="grid gap-6">
        <Panel title={`Property Detail / Lead Fit ${selectedScore}%`} icon={Building2}><PropertyForm property={props.selectedProperty} masterData={props.state.masterData} allProperties={props.state.properties} currentUser={props.currentUser} onSave={props.updateProperty} /></Panel>
        <PropertyLeadPanel state={props.state} visibleInterested={visibleInterested} suggestedLeads={suggestedLeads} property={props.selectedProperty} markInterested={props.markInterested} createSchedule={props.createSchedule} />
        <Timeline state={props.state} propertyId={props.selectedProperty.id} />
      </div>
    </SplitList>
  );
}

function PropertyLeadPanel(props: {
  state: CrmState;
  visibleInterested: InterestedProperty[];
  suggestedLeads: { lead: Lead; score: number }[];
  property: Property;
  markInterested: (leadId: string, propertyId: string) => void;
  createSchedule: (interest: InterestedProperty, type?: string) => void;
}) {
  const totalInterested = props.state.interestedProperties.filter((interest) => interest.propertyId === props.property.id).length;
  return (
    <Panel title={`Interested Leads / Total ${totalInterested}`} icon={Users}>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-3">
          <h3 className="text-sm font-semibold">Visible interested leads</h3>
          {!props.visibleInterested.length ? <EmptyState text="No accessible interested leads." /> : props.visibleInterested.map((interest) => {
            const lead = props.state.leads.find((item) => item.id === interest.leadId);
            return <div className="rounded-md bg-background p-3 text-sm" key={interest.id}><p className="font-medium">{lead?.name || "Restricted lead"} / {interest.status}</p><p className="text-muted-foreground">{interest.note || "No note"}</p><button className="btn secondary mt-3" onClick={() => props.createSchedule(interest)}><CalendarPlus size={16} />Schedule</button></div>;
          })}
        </div>
        <div className="grid content-start gap-3">
          <h3 className="text-sm font-semibold">Suggested leads</h3>
          {!props.suggestedLeads.length ? <EmptyState text="No suggested leads match this property yet." /> : props.suggestedLeads.map((item) => (
            <div className="flex items-center justify-between rounded-md bg-background p-3 text-sm" key={item.lead.id}>
              <div><p className="font-medium">{item.lead.name}</p><p className="text-muted-foreground">{item.lead.status} / {money(item.lead.budget)}</p></div>
              <div className="flex items-center gap-2"><span className="font-semibold">{numberText(item.score)}%</span><IconButton label="Mark interested" onClick={() => props.markInterested(item.lead.id, props.property.id)}><Plus size={15} /></IconButton></div>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

function Matching({ state, selectedLeadId, setSelectedLeadId, matches, markInterested }: { state: CrmState; selectedLeadId: string; setSelectedLeadId: (id: string) => void; matches: PropertyMatch[]; markInterested: (leadId: string, propertyId: string) => void }) {
  return (
    <div className="grid gap-5">
      <Panel title="Property Matching Engine" icon={Sparkles}>
        <Select label="Lead" value={selectedLeadId} onChange={setSelectedLeadId}>{state.leads.map((lead) => <option key={lead.id} value={lead.id}>{lead.name}</option>)}</Select>
      </Panel>
      <div className="grid gap-4 lg:grid-cols-2">
        {!matches.length ? <EmptyState text="No high-confidence matches yet." /> : matches.map((property) => (
          <div className="rounded-lg bg-card p-5 shadow-sm" key={property.id}>
            <div className="flex items-start justify-between gap-3"><div><p className="text-lg font-semibold">{property.name}</p><p className="text-sm text-muted-foreground">{property.location} / {property.type} / {money(property.price)}</p></div><span className="rounded-md bg-primary px-3 py-1 text-sm font-semibold text-primary-foreground">{numberText(property.matchScore)}%</span></div>
            <MatchBreakdown match={property} />
            <button className="btn secondary mt-4" onClick={() => markInterested(selectedLeadId, property.id)}><Sparkles size={16} />Mark interested</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchBreakdown({ match }: { match: PropertyMatch }) {
  return (
    <div className="mt-4 grid gap-2">
      {match.matchBreakdown.map((line) => (
        <div key={line.label} className="grid grid-cols-[82px_1fr_52px] items-center gap-2 text-sm">
          <span>{line.label}</span>
          <div className="h-2 rounded-full bg-muted"><div className="h-2 rounded-full bg-[#d7b46a]" style={{ width: `${Math.round(line.raw * 100)}%` }} /></div>
          <span className="text-right text-muted-foreground">{Math.round(line.raw * line.weight)}/{line.weight}</span>
        </div>
      ))}
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
        {match.matchExplanation.slice(0, 4).map((line) => <span className="rounded-md bg-muted px-2 py-1" key={line}>{line}</span>)}
        {match.missingRequirements.map((line) => <span className="rounded-md bg-destructive/10 px-2 py-1 text-destructive" key={line}>{line}</span>)}
      </div>
    </div>
  );
}

function RadarScore({ match }: { match: PropertyMatch }) {
  const center = 70;
  const radius = 42;
  const points = match.matchBreakdown.map((line, index) => {
    const angle = (Math.PI * 2 * index) / match.matchBreakdown.length - Math.PI / 2;
    const value = Math.max(0.08, Math.min(1, line.raw));
    return `${center + Math.cos(angle) * radius * value},${center + Math.sin(angle) * radius * value}`;
  }).join(" ");
  const axes = match.matchBreakdown.map((line, index) => {
    const angle = (Math.PI * 2 * index) / match.matchBreakdown.length - Math.PI / 2;
    return {
      line,
      x: center + Math.cos(angle) * radius,
      y: center + Math.sin(angle) * radius,
      labelX: center + Math.cos(angle) * (radius + 22),
      labelY: center + Math.sin(angle) * (radius + 18)
    };
  });
  return (
    <div className="grid justify-items-center gap-2">
      <svg viewBox="0 0 140 140" className="h-40 w-40">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth="1" />
        <circle cx={center} cy={center} r={radius * 0.66} fill="none" stroke="hsl(var(--border))" strokeWidth="1" />
        <circle cx={center} cy={center} r={radius * 0.33} fill="none" stroke="hsl(var(--border))" strokeWidth="1" />
        {axes.map(({ line, x, y }) => <line key={line.label} x1={center} y1={center} x2={x} y2={y} stroke="hsl(var(--border))" strokeWidth="1" />)}
        <polygon points={points} fill="rgba(215,180,106,0.35)" stroke="#b58a34" strokeWidth="2" />
        {axes.map(({ line, labelX, labelY }) => <text key={`${line.label}-label`} x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle" className="fill-muted-foreground text-[8px] font-semibold">{line.label}</text>)}
      </svg>
      <span className="rounded-md bg-primary px-3 py-1 text-sm font-semibold text-primary-foreground">{numberText(match.matchScore)}%</span>
    </div>
  );
}

function MissingRequirements({ match }: { match: PropertyMatch }) {
  if (!match.missingRequirements.length) return <p className="mt-3 text-sm text-muted-foreground">No missing requirements detected.</p>;
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold uppercase text-muted-foreground">Missing requirements</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {match.missingRequirements.map((line) => <span className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive" key={line}>{line}</span>)}
      </div>
    </div>
  );
}

function Kanban({ columns, fallbackColumn, children }: { columns: string[]; fallbackColumn: string; children: (column: string) => ReactNode }) {
  const allColumns = columns.length ? columns : [fallbackColumn];
  return (
    <div className="grid gap-4 overflow-x-auto md:grid-cols-2 xl:grid-cols-5">
      {allColumns.map((column) => (
        <section className="min-w-64 rounded-lg bg-card p-3 shadow-sm" key={column}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="font-semibold">{column}</h2>
          </div>
          <div className="grid gap-3">{children(column)}</div>
        </section>
      ))}
    </div>
  );
}

function ScheduleList({ state, masterData, updateViewing, deleteViewing, title = "Schedule / Calendar" }: { state: CrmState; masterData: MasterData; updateViewing: (viewing: Viewing) => void; deleteViewing: (viewingId: string) => void; title?: string }) {
  return (
    <Panel title={title} icon={CalendarClock}>
      {!state.viewings.length ? <EmptyState text="No scheduled items." /> : (
        <div className="overflow-x-auto">
          <table className="data-table min-w-[980px]">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Property</th>
                <th>Date</th>
                <th>Time</th>
                <th>Type</th>
                <th>Status</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.viewings.map((item) => <ViewingTableRow key={item.id} item={item} state={state} masterData={masterData} updateViewing={updateViewing} deleteViewing={deleteViewing} />)}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function ViewingTableRow({ item, state, masterData, updateViewing, deleteViewing }: { item: Viewing; state: CrmState; masterData: MasterData; updateViewing: (viewing: Viewing) => void; deleteViewing: (viewingId: string) => void }) {
  const [draft, setDraft] = useState(item);
  const [date, setDate] = useState(datePart(item.startsAt));
  const [time, setTime] = useState(timePart(item.startsAt));
  useEffect(() => {
    setDraft(item);
    setDate(datePart(item.startsAt));
    setTime(timePart(item.startsAt));
  }, [item]);
  return (
    <tr>
      <td><select className="field min-w-40" value={draft.leadId} onChange={(event) => setDraft({ ...draft, leadId: event.target.value })}>{state.leads.map((lead) => <option key={lead.id} value={lead.id}>{lead.name || lead.email || lead.id}</option>)}</select></td>
      <td><select className="field min-w-44" value={draft.propertyId} onChange={(event) => setDraft({ ...draft, propertyId: event.target.value, location: state.properties.find((property) => property.id === event.target.value)?.location || draft.location })}>{state.properties.map((property) => <option key={property.id} value={property.id}>{property.name || property.code}</option>)}</select></td>
      <td><input className="field min-w-36" type="date" value={date} onChange={(event) => setDate(event.target.value)} /></td>
      <td><input className="field min-w-28" type="time" value={time} onChange={(event) => setTime(event.target.value)} /></td>
      <td><select className="field min-w-40" value={draft.type} onChange={(event) => setDraft({ ...draft, type: event.target.value })}>{(masterData.scheduleTypes || []).map((value) => <option key={value}>{value}</option>)}</select></td>
      <td><select className="field min-w-36" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>{(masterData.scheduleStatuses || masterData.viewingStatuses || []).map((value) => <option key={value}>{value}</option>)}</select></td>
      <td><input className="field min-w-48" value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} /></td>
      <td><div className="flex gap-2"><button className="btn secondary" onClick={() => updateViewing({ ...draft, startsAt: combineDateTime(date, time) })}><CheckCircle2 size={16} />Save</button><button className="btn danger" onClick={() => deleteViewing(item.id)}><Trash2 size={16} />Delete</button></div></td>
    </tr>
  );
}

function FollowUpList({ state, masterData, updateActivity, deleteActivity, title = "Follow-up Tracker" }: { state: CrmState; masterData: MasterData; updateActivity: (activity: Activity) => void; deleteActivity: (activityId: string) => void; title?: string }) {
  return (
    <Panel title={title} icon={ListChecks}>
      {!state.activities.length ? <EmptyState text="No follow-ups yet." /> : (
        <div className="overflow-x-auto">
          <table className="data-table min-w-[880px]">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Property</th>
                <th>Date</th>
                <th>Time</th>
                <th>Type</th>
                <th>Status</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.activities.map((item) => <FollowUpTableRow key={item.id} item={item} state={state} masterData={masterData} updateActivity={updateActivity} deleteActivity={deleteActivity} />)}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function FollowUpTableRow({ item, state, masterData, updateActivity, deleteActivity }: { item: Activity; state: CrmState; masterData: MasterData; updateActivity: (activity: Activity) => void; deleteActivity: (activityId: string) => void }) {
  const [draft, setDraft] = useState(item);
  const [date, setDate] = useState(datePart(item.dueAt));
  const [time, setTime] = useState(timePart(item.dueAt));
  useEffect(() => {
    setDraft(item);
    setDate(datePart(item.dueAt));
    setTime(timePart(item.dueAt));
  }, [item]);
  return (
    <tr className={item.status !== "Completed" && isOverdue(item.dueAt) ? "bg-destructive/10" : undefined}>
      <td><select className="field min-w-40" value={draft.leadId} onChange={(event) => setDraft({ ...draft, leadId: event.target.value })}>{state.leads.map((lead) => <option key={lead.id} value={lead.id}>{lead.name || lead.email || lead.id}</option>)}</select></td>
      <td><select className="field min-w-44" value={draft.propertyId || ""} onChange={(event) => setDraft({ ...draft, propertyId: event.target.value || undefined })}><option value="">No property</option>{state.properties.map((property) => <option key={property.id} value={property.id}>{property.name || property.code}</option>)}</select></td>
      <td><input className="field min-w-36" type="date" value={date} onChange={(event) => setDate(event.target.value)} /></td>
      <td><input className="field min-w-28" type="time" value={time} onChange={(event) => setTime(event.target.value)} /></td>
      <td><select className="field min-w-36" value={draft.type} onChange={(event) => setDraft({ ...draft, type: event.target.value })}>{(masterData.activityTypes || []).map((value) => <option key={value}>{value}</option>)}</select></td>
      <td><select className="field min-w-36" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>{(masterData.activityStatuses || []).map((value) => <option key={value}>{value}</option>)}</select></td>
      <td><input className="field min-w-48" value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} /></td>
      <td><div className="flex gap-2"><button className="btn secondary" onClick={() => updateActivity({ ...draft, dueAt: combineDateTime(date, time) })}><CheckCircle2 size={16} />Save</button><button className="btn danger" onClick={() => deleteActivity(item.id)}><Trash2 size={16} />Delete</button></div></td>
    </tr>
  );
}

function Pipeline({ state }: { state: CrmState }) {
  const stages = state.masterData.pipelineStages || [];
  return <div className="grid gap-4 overflow-x-auto md:grid-cols-4 xl:grid-cols-8">{stages.map((stage) => <div className="min-w-52 rounded-lg bg-card p-3" key={stage}><p className="mb-3 font-semibold">{stage}</p>{state.opportunities.filter((opp) => opp.stage === stage).map((opp) => <div className="mb-2 rounded-md bg-background p-3 text-sm" key={opp.id}>{state.leads.find((lead) => lead.id === opp.leadId)?.name}<p className="text-muted-foreground">{money(opp.value)}</p></div>)}</div>)}</div>;
}

function CommissionList({ state }: { state: CrmState }) {
  return <Panel title="Commission Tracking" icon={BadgeDollarSign}><div className="grid gap-2">{state.commissions.map((item) => <div className="rounded-md bg-background p-3" key={item.id}>{money(item.propertyValue)} / {item.commissionRate}% / {money(item.commissionAmount)} / {item.paymentStatus}</div>)}</div></Panel>;
}

function Configuration({ state, persist, currentUser }: { state: CrmState; persist: (state: CrmState, action?: string) => Promise<void>; currentUser: UserProfile }) {
  const [tab, setTab] = useState<"master" | "users">("master");
  return (
    <div className="grid min-w-0 gap-5">
      <div className="flex flex-wrap gap-2">
        <button className={cn("btn secondary", tab === "master" && "bg-primary text-primary-foreground")} onClick={() => setTab("master")}><Database size={16} />Master data</button>
        <button className={cn("btn secondary", tab === "users" && "bg-primary text-primary-foreground")} onClick={() => setTab("users")}><Users size={16} />Users</button>
      </div>
      {tab === "master" ? <MasterData state={state} persist={persist} currentUser={currentUser} /> : <Admin state={state} persist={persist} currentUser={currentUser} />}
    </div>
  );
}

function SortableHeader<T extends string>({ label, column, sortKey, direction, onSort }: { label: string; column: T; sortKey: T; direction: SortDirection; onSort: (column: T) => void }) {
  const active = sortKey === column;
  return (
    <button className="inline-flex items-center gap-1 font-semibold text-foreground" onClick={() => onSort(column)}>
      {label}
      <span className="text-xs text-muted-foreground">{active ? direction === "asc" ? "▲" : "▼" : "↕"}</span>
    </button>
  );
}

function MasterData({ state, persist, currentUser }: { state: CrmState; persist: (state: CrmState, action?: string) => Promise<void>; currentUser: UserProfile }) {
  const masterKeys = useMemo(() => Object.keys(state.masterData).filter((item) => item !== masterUsageMetaKey).sort((a, b) => sortText(masterLabel(a), masterLabel(b))), [state.masterData]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldUsage, setNewFieldUsage] = useState("");
  const [valueSort, setValueSort] = useState<{ key: "position" | "value"; direction: SortDirection }>({ key: "value", direction: "asc" });
  const key = selectedKey || "";
  const values = key ? state.masterData[key] || [] : [];
  const [draftValues, setDraftValues] = useState<string[]>(values);
  useEffect(() => {
    if (selectedKey && !masterKeys.includes(selectedKey)) setSelectedKey(null);
  }, [selectedKey, masterKeys]);
  useEffect(() => {
    setDraftValues(values);
  }, [key, state.masterData]);
  function addField() {
    const label = newFieldLabel.trim();
    if (!label || !newFieldUsage) return;
    const nextKey = createMasterKey(label, state.masterData);
    const nextMeta = textFromList([...(state.masterData[masterUsageMetaKey] || []), `${nextKey}::${newFieldUsage}`]);
    void persist(addAudit({ ...state, masterData: { ...state.masterData, [nextKey]: [], [masterUsageMetaKey]: listFromText(nextMeta) } }, "create", "master_data", nextKey, currentUser.id, undefined, []), "Master data field added");
    setSelectedKey(nextKey);
    setNewFieldLabel("");
    setNewFieldUsage("");
  }
  function saveValues(nextValues: string[]) {
    if (!key) return;
    const deduped = Array.from(new Set(nextValues.map((value) => value.trim()).filter(Boolean))).sort(sortText);
    void persist(addAudit({ ...state, masterData: { ...state.masterData, [key]: deduped } }, "update", "master_data", key, currentUser.id, values, deduped), "Master data saved");
  }
  function saveRow(index: number) {
    const next = [...values];
    if (index >= next.length) next.push(draftValues[index] || "");
    else next[index] = draftValues[index] || "";
    saveValues(next);
  }
  function deleteRow(index: number) {
    if (index >= values.length) {
      setDraftValues(draftValues.filter((_, itemIndex) => itemIndex !== index));
      return;
    }
    saveValues(values.filter((_, itemIndex) => itemIndex !== index));
  }
  function sortValueColumn(column: "position" | "value") {
    setValueSort((current) => ({ key: column, direction: current.key === column && current.direction === "asc" ? "desc" : "asc" }));
  }
  const sortedRows = draftValues
    .map((value, index) => ({ value, index }))
    .sort((a, b) => valueSort.key === "position" ? compareValues(a.index + 1, b.index + 1, valueSort.direction) : compareValues(a.value, b.value, valueSort.direction));
  if (!selectedKey) {
    return (
      <Panel title="Custom Master Data" icon={Database}>
        <div className="grid gap-4">
          <div className="rounded-lg bg-background p-4 shadow-sm">
            <div className="grid gap-3 lg:grid-cols-[1fr_260px_auto] lg:items-end">
              <Input label="Add new field" value={newFieldLabel} placeholder="Example: Pet Policies" onChange={setNewFieldLabel} />
              <Select label="Connected to" value={newFieldUsage} onChange={setNewFieldUsage}><option value="">Select section</option>{masterConnectionOptions.map((value) => <option key={value}>{value}</option>)}</Select>
              <button className="btn add" onClick={addField} disabled={!newFieldLabel.trim() || !newFieldUsage}><Plus size={16} />Add field</button>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {masterKeys.map((item) => (
              <button className="rounded-lg bg-background p-4 text-left shadow-sm transition hover:bg-white hover:shadow-md" key={item} onClick={() => setSelectedKey(item)}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{masterLabel(item)}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">{numberText(state.masterData[item]?.length || 0)} values</p>
                  </div>
                  <ChevronRight size={18} className="mt-1 text-muted-foreground" />
                </div>
                <UsageHint masterKey={item} masterData={state.masterData} />
              </button>
            ))}
          </div>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title={masterLabel(key)} icon={Database} action={<button className="btn secondary" onClick={() => setSelectedKey(null)}><ArrowLeft size={16} />Back to fields</button>}>
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-background p-4 shadow-sm">
          <UsageHint masterKey={key} masterData={state.masterData} />
          <div className="rounded-md bg-[#f1dfbd]/70 px-3 py-2 text-sm font-semibold text-[#6f5116]">{numberText(values.length)} saved values</div>
        </div>
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/50 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold">{masterLabel(key)}</h3>
              <p className="text-xs text-muted-foreground">Edit a value, then save that row. Use Back to return to the field list.</p>
            </div>
            <button className="btn add" onClick={() => setDraftValues([...draftValues, ""])}><Plus size={16} />Add value</button>
          </div>
          {!draftValues.length ? <EmptyState text="No values yet." /> : (
            <div className="overflow-x-auto">
              <table className="data-table min-w-[620px]">
                <thead>
                  <tr>
                    <th><SortableHeader label="No." column="position" sortKey={valueSort.key} direction={valueSort.direction} onSort={sortValueColumn} /></th>
                    <th><SortableHeader label="Value" column="value" sortKey={valueSort.key} direction={valueSort.direction} onSort={sortValueColumn} /></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => (
                    <tr key={`${key}-${row.index}`}>
                      <td className="text-sm text-muted-foreground">{numberText(row.index + 1)}</td>
                      <td><Input value={row.value} placeholder="Value" onChange={(next) => setDraftValues(draftValues.map((item, itemIndex) => itemIndex === row.index ? next : item))} /></td>
                      <td><div className="flex gap-2"><button className="btn secondary" onClick={() => saveRow(row.index)}><CheckCircle2 size={16} />Save</button><button className="btn danger" onClick={() => deleteRow(row.index)}><Trash2 size={16} />Delete</button></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

function UsageHint({ masterKey, masterData }: { masterKey: string; masterData: MasterData }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span className="font-medium text-[#7a5a14]">Connected to</span>
      {masterUsage(masterKey, masterData).map((section) => <span className="subtle-chip" key={section}>{section}</span>)}
    </div>
  );
}

function Admin({ state, persist, currentUser }: { state: CrmState; persist: (state: CrmState, action?: string) => Promise<void>; currentUser: UserProfile }) {
  type UserSortKey = "name" | "email" | "role" | "teams" | "status";
  const [sort, setSort] = useState<{ key: UserSortKey; direction: SortDirection }>({ key: "name", direction: "asc" });
  const [teamFilter, setTeamFilter] = useState("All");
  const teams = state.masterData.teams || [];
  const sortedUsers = useMemo(() => {
    const filtered = teamFilter === "All" ? state.users : state.users.filter((user) => userTeams(user).includes(teamFilter));
    return [...filtered].sort((a, b) => {
      if (sort.key === "email") return compareValues(a.email, b.email, sort.direction);
      if (sort.key === "role") return compareValues(a.role, b.role, sort.direction) || compareValues(a.name, b.name, sort.direction);
      if (sort.key === "status") return compareValues(a.active ? "Active" : "Inactive", b.active ? "Active" : "Inactive", sort.direction) || compareValues(a.name, b.name, sort.direction);
      if (sort.key === "teams") return compareValues(userTeams(a).join(", "), userTeams(b).join(", "), sort.direction) || compareValues(a.name, b.name, sort.direction);
      return compareValues(a.name, b.name, sort.direction);
    });
  }, [state.users, sort, teamFilter]);
  function sortUserColumn(column: UserSortKey) {
    setSort((current) => ({ key: column, direction: current.key === column && current.direction === "asc" ? "desc" : "asc" }));
  }
  function updateUser(user: UserProfile) {
    void persist(addAudit({ ...state, users: state.users.map((item) => item.id === user.id ? user : item) }, "update", "profile", user.id, currentUser.id, undefined, user), "User saved");
  }
  function deleteUser(user: UserProfile) {
    void persist(addAudit({ ...state, users: state.users.filter((item) => item.id !== user.id) }, "delete", "profile", user.id, currentUser.id, user), "User deleted");
  }
  function addUser() {
    const now = new Date().toISOString();
    const user: UserProfile = { id: id("usr"), name: "New User", email: "new.user@example.com", role: "Agent", active: true, teamId: state.masterData.teams?.[0] || "Sukhumvit Team", teamIds: [state.masterData.teams?.[0] || "Sukhumvit Team"], createdAt: now };
    void persist(addAudit({ ...state, users: [user, ...state.users] }, "create", "profile", user.id, currentUser.id, undefined, user), "User added");
  }
  return (
    <Panel title="Users" icon={Settings} action={<button className="btn add" onClick={addUser}><Plus size={16} />Add user</button>}>
      <div className="mb-4 grid gap-3 rounded-lg bg-background p-4 shadow-sm md:grid-cols-[minmax(220px,320px)_1fr] md:items-end">
        <Select label="Filter teams" value={teamFilter} onChange={setTeamFilter}>
          <option value="All">All teams</option>
          {teams.map((team) => <option key={team} value={team}>{team}</option>)}
        </Select>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{numberText(sortedUsers.length)} users</span>
          {teamFilter === "All" ? <span>showing every team</span> : <span>filtered by</span>}
          {teamFilter !== "All" ? <span className="soft-chip active">{teamFilter}</span> : null}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="data-table users-table min-w-[1120px] table-fixed">
          <colgroup>
            <col style={{ width: "18rem" }} />
            <col style={{ width: "20rem" }} />
            <col style={{ width: "9rem" }} />
            <col style={{ width: "25rem" }} />
            <col style={{ width: "9rem" }} />
            <col style={{ width: "13rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th><SortableHeader label="Name" column="name" sortKey={sort.key} direction={sort.direction} onSort={sortUserColumn} /></th>
              <th><SortableHeader label="Email" column="email" sortKey={sort.key} direction={sort.direction} onSort={sortUserColumn} /></th>
              <th><SortableHeader label="Role" column="role" sortKey={sort.key} direction={sort.direction} onSort={sortUserColumn} /></th>
              <th><SortableHeader label="Teams" column="teams" sortKey={sort.key} direction={sort.direction} onSort={sortUserColumn} /></th>
              <th><SortableHeader label="Status" column="status" sortKey={sort.key} direction={sort.direction} onSort={sortUserColumn} /></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                teams={teams}
                onSave={updateUser}
                onDelete={deleteUser}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function UserRow({ user, teams, onSave, onDelete }: { user: UserProfile; teams: string[]; onSave: (user: UserProfile) => void; onDelete: (user: UserProfile) => void }) {
  const [draft, setDraft] = useState(user);
  const [teamToAdd, setTeamToAdd] = useState("");
  useEffect(() => {
    setDraft(user);
    setTeamToAdd("");
  }, [user]);
  const selectedTeams = userTeams(draft);
  const availableTeams = teams.filter((team) => !selectedTeams.includes(team));
  function removeTeam(team: string) {
    if (selectedTeams.length <= 1) return;
    const nextTeams = selectedTeams.filter((item) => item !== team);
    setDraft({ ...draft, teamIds: nextTeams, teamId: nextTeams[0] || draft.teamId });
  }
  function addTeam(team: string) {
    if (!team || selectedTeams.includes(team)) return;
    const nextTeams = [...selectedTeams, team];
    setDraft({ ...draft, teamIds: nextTeams, teamId: nextTeams[0] || team });
    setTeamToAdd("");
  }
  function save() {
    const teamIds = selectedTeams.length ? selectedTeams : teams.slice(0, 1);
    onSave({ ...draft, teamIds, teamId: teamIds[0] || draft.teamId });
  }
  return (
    <tr>
      <td><Input value={draft.name} onChange={(value) => setDraft({ ...draft, name: value })} /></td>
      <td><Input value={draft.email} onChange={(value) => setDraft({ ...draft, email: value })} /></td>
      <td><select className="field min-w-32" value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as Role })}><option>Admin</option><option>Manager</option><option>Agent</option></select></td>
      <td>
        <div className="grid min-w-0 gap-2">
          <div className="flex flex-wrap gap-2">
            {selectedTeams.map((team) => (
            <button
              className="soft-chip active"
              key={team}
              onClick={() => removeTeam(team)}
              title={selectedTeams.length <= 1 ? "At least one team is required" : `Remove ${team}`}
              type="button"
            >
              {team}{selectedTeams.length > 1 ? <X size={12} /> : null}
            </button>
          ))}
            {!selectedTeams.length ? <span className="text-xs text-muted-foreground">No teams assigned</span> : null}
          </div>
          {availableTeams.length ? (
            <select className="field h-9 min-h-9" value={teamToAdd} onChange={(event) => { setTeamToAdd(event.target.value); addTeam(event.target.value); }}>
              <option value="">Add team</option>
              {availableTeams.map((team) => <option key={team} value={team}>{team}</option>)}
            </select>
          ) : null}
        </div>
      </td>
      <td><select className="field min-w-32" value={String(draft.active)} onChange={(event) => setDraft({ ...draft, active: event.target.value === "true" })}><option value="true">Active</option><option value="false">Inactive</option></select></td>
      <td><div className="flex gap-2"><button className="btn secondary" onClick={save}><CheckCircle2 size={16} />Save</button><button className="btn danger" onClick={() => onDelete(user)}><Trash2 size={16} />Delete</button></div></td>
    </tr>
  );
}

function LeadForm({ lead, masterData, users, allLeads, currentUser, canEdit, onSave }: { lead: Lead; masterData: MasterData; users: UserProfile[]; allLeads: Lead[]; currentUser: UserProfile; canEdit: boolean; onSave: (lead: Lead) => void }) {
  const [draft, setDraft] = useState(lead);
  useEffect(() => setDraft(lead), [lead]);
  const duplicates = findLeadDuplicates(allLeads, draft);
  return (
    <div className="grid gap-4">
      {duplicates.length ? <DuplicateWarning count={duplicates.length} visible={duplicates.filter((item) => canViewLead(currentUser, item)).map((item) => item.name || item.email || item.phone)} hiddenCount={duplicates.filter((item) => !canViewLead(currentUser, item)).length} /> : null}
      <FormGrid>
        <Input label="Name" value={draft.name} onChange={(value) => setDraft({ ...draft, name: value })} disabled={!canEdit} />
        <Input label="Phone" value={draft.phone} onChange={(value) => setDraft({ ...draft, phone: value })} disabled={!canEdit} />
        <Input label="Email" value={draft.email} onChange={(value) => setDraft({ ...draft, email: value })} disabled={!canEdit} />
        <CreatableInput label="Nationality" value={draft.nationality} options={masterData.nationalities || []} onChange={(value) => setDraft({ ...draft, nationality: value })} disabled={!canEdit} />
        <CreatableInput label="Source" value={draft.source} options={masterData.leadSources || []} onChange={(value) => setDraft({ ...draft, source: value })} disabled={!canEdit} />
        <Select label="Status" value={draft.status} onChange={(value) => setDraft({ ...draft, status: value })} disabled={!canEdit}>{(masterData.leadStatuses || []).map((value) => <option key={value}>{value}</option>)}</Select>
        <Select label="Temperature" value={draft.temperature} onChange={(value) => setDraft({ ...draft, temperature: value })} disabled={!canEdit}>{(masterData.leadTemperatures || []).map((value) => <option key={value}>{value}</option>)}</Select>
        <Input label="Budget (THB)" type="number" value={draft.budget} onChange={(value) => setDraft({ ...draft, budget: Number(value) })} disabled={!canEdit} />
        <Input label="Timeline" value={draft.timeline} onChange={(value) => setDraft({ ...draft, timeline: value })} disabled={!canEdit} />
        <Select label="Agent" value={draft.agentId} onChange={(value) => setDraft({ ...draft, agentId: value })} disabled={!canEdit}>{users.filter((user) => user.active).map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</Select>
        <TextArea label="Note" value={draft.note} onChange={(value) => setDraft({ ...draft, note: value })} disabled={!canEdit} />
        <button className="btn md:col-span-2" onClick={() => onSave(draft)} disabled={!canEdit}><FormattedMessage id="save" /></button>
      </FormGrid>
    </div>
  );
}

function RequirementForm({ requirement, masterData, onSave }: { requirement: Requirement; masterData: MasterData; onSave: (requirement: Requirement) => void }) {
  const [draft, setDraft] = useState(requirement);
  useEffect(() => setDraft(requirement), [requirement]);
  return (
    <FormGrid>
      <Select label="Purpose" value={draft.purpose} onChange={(value) => setDraft({ ...draft, purpose: value })}>{(masterData.propertyPurposes || []).map((value) => <option key={value}>{value}</option>)}</Select>
      <CreatableInput label="Location" value={draft.location} options={[]} onChange={(value) => setDraft({ ...draft, location: value })} />
      <CreatableInput label="Preferred Areas" value={draft.preferredAreas} options={[]} onChange={(value) => setDraft({ ...draft, preferredAreas: value })} />
      <CreatableInput label="Near BTS" value={draft.nearBts} options={masterData.btsStations || []} onChange={(value) => setDraft({ ...draft, nearBts: value })} />
      <CreatableInput label="Near MRT" value={draft.nearMrt} options={masterData.mrtStations || []} onChange={(value) => setDraft({ ...draft, nearMrt: value })} />
      <CreatableInput label="Near Airport Link" value={draft.nearAirportLink} options={masterData.airportLinkStations || []} onChange={(value) => setDraft({ ...draft, nearAirportLink: value })} />
      <CreatableInput label="Near Airport" value={draft.nearAirport} options={masterData.airports || []} onChange={(value) => setDraft({ ...draft, nearAirport: value })} />
      <Select label="Property Type" value={draft.propertyType} onChange={(value) => setDraft({ ...draft, propertyType: value })}>{(masterData.propertyTypes || []).map((value) => <option key={value}>{value}</option>)}</Select>
      <CreatableInput label="Development" value={draft.development} options={[]} onChange={(value) => setDraft({ ...draft, development: value })} />
      <Input label="Budget Min" type="number" value={draft.budgetMin} onChange={(value) => setDraft({ ...draft, budgetMin: Number(value) })} />
      <Input label="Budget Max" type="number" value={draft.budgetMax} onChange={(value) => setDraft({ ...draft, budgetMax: Number(value) })} />
      <Input label="Bedrooms" type="number" value={draft.bedrooms} onChange={(value) => setDraft({ ...draft, bedrooms: Number(value) })} />
      <Input label="Bathrooms" type="number" value={draft.bathrooms} onChange={(value) => setDraft({ ...draft, bathrooms: Number(value) })} />
      <Input label="Living rooms" type="number" value={draft.livingRooms} onChange={(value) => setDraft({ ...draft, livingRooms: Number(value) })} />
      <Input label="Kitchens" type="number" value={draft.kitchens} onChange={(value) => setDraft({ ...draft, kitchens: Number(value) })} />
      <Input label="Other rooms" type="number" value={draft.otherRooms} onChange={(value) => setDraft({ ...draft, otherRooms: Number(value) })} />
      <Input label="Min Size" type="number" value={draft.size} onChange={(value) => setDraft({ ...draft, size: Number(value) })} />
      <Input label="Car parking" type="number" value={draft.parkingCars} onChange={(value) => setDraft({ ...draft, parkingCars: Number(value) })} />
      <Input label="Motorcycle parking" type="number" value={draft.parkingMotorcycles} onChange={(value) => setDraft({ ...draft, parkingMotorcycles: Number(value) })} />
      <Input label="Bicycle parking" type="number" value={draft.parkingBicycles} onChange={(value) => setDraft({ ...draft, parkingBicycles: Number(value) })} />
      <Checkbox label="Pet friendly" checked={draft.petFriendly} onChange={(value) => setDraft({ ...draft, petFriendly: value })} />
      <Checkbox label="BTS/MRT access" checked={draft.transitAccess} onChange={(value) => setDraft({ ...draft, transitAccess: value })} />
      <Checkbox label="Pool" checked={draft.pool} onChange={(value) => setDraft({ ...draft, pool: value })} />
      <Checkbox label="Fitness" checked={draft.fitness} onChange={(value) => setDraft({ ...draft, fitness: value })} />
      <Checkbox label="Co-working space" checked={draft.coWorkingSpace} onChange={(value) => setDraft({ ...draft, coWorkingSpace: value })} />
      <Checkbox label="Garden" checked={draft.garden} onChange={(value) => setDraft({ ...draft, garden: value })} />
      <CreatableInput label="Facilities" value={draft.facilities} options={masterData.facilities || []} onChange={(value) => setDraft({ ...draft, facilities: value })} />
      <Input label="Timeline" value={draft.timeline} onChange={(value) => setDraft({ ...draft, timeline: value })} />
      <button className="btn md:col-span-2" onClick={() => onSave(draft)}><FormattedMessage id="save" /></button>
    </FormGrid>
  );
}

function PropertyForm({ property, masterData, allProperties, users = [], currentUser, onSave }: { property: Property; masterData: MasterData; allProperties: Property[]; users?: UserProfile[]; currentUser: UserProfile; onSave: (property: Property) => void }) {
  const [draft, setDraft] = useState(property);
  useEffect(() => setDraft(property), [property]);
  const duplicates = findPropertyDuplicates(allProperties, draft);
  const token = draft.publicToken || id("pub");
  const publicUrl = publicPropertyUrl(token);
  const propertyNameOptions = Array.from(new Set(allProperties.map((item) => item.name).filter(Boolean))).sort(sortText);
  const developmentOptions = Array.from(new Set([...(masterData.propertyDevelopments || []), ...allProperties.map((item) => item.development).filter(Boolean), "Narai Property"])).sort(sortText);
  function generateAffiliate() {
    if (draft.visibility !== "Public") return;
    const nextToken = draft.publicToken || id("pub");
    const base = publicPropertyUrl(nextToken);
    setDraft({ ...draft, publicToken: nextToken, publicVisible: true, visibility: "Public", affiliateLink: `${base}?agent=${encodeURIComponent(currentUser.id)}&utm_source=affiliate` });
  }
  return (
    <div className="grid gap-4">
      {duplicates.length ? <DuplicateWarning count={duplicates.length} visible={duplicates.map((item) => item.name || item.code)} hiddenCount={0} /> : null}
      <FormGrid>
        <Input label="Property ID" value={draft.code} onChange={(value) => setDraft({ ...draft, code: value })} />
        <CreatableInput label="Property Name" value={draft.name} options={propertyNameOptions} onChange={(value) => setDraft({ ...draft, name: value })} />
        <Select label="Purpose" value={draft.purpose} onChange={(value) => setDraft({ ...draft, purpose: value })}>{(masterData.propertyPurposes || []).map((value) => <option key={value}>{value}</option>)}</Select>
        <Select label="Type" value={draft.type} onChange={(value) => setDraft({ ...draft, type: value })}>{(masterData.propertyTypes || []).map((value) => <option key={value}>{value}</option>)}</Select>
        <CreatableInput label="Development" value={draft.development} options={developmentOptions} onChange={(value) => setDraft({ ...draft, development: value })} />
        <Input label="Building" value={draft.building} onChange={(value) => setDraft({ ...draft, building: value })} />
        <Input label="Floor" value={draft.floor} onChange={(value) => setDraft({ ...draft, floor: value })} />
        <CreatableInput label="Location" value={draft.location} options={[]} onChange={(value) => setDraft({ ...draft, location: value })} />
        <CreatableInput label="Near BTS" value={draft.nearBts} options={masterData.btsStations || []} onChange={(value) => setDraft({ ...draft, nearBts: value })} />
        <CreatableInput label="Near MRT" value={draft.nearMrt} options={masterData.mrtStations || []} onChange={(value) => setDraft({ ...draft, nearMrt: value })} />
        <Select label="Source" value={draft.source} onChange={(value) => setDraft({ ...draft, source: value })}>{(masterData.propertySources || []).map((value) => <option key={value}>{value}</option>)}</Select>
        <Select label="Status" value={draft.status} onChange={(value) => setDraft({ ...draft, status: value })}>{(masterData.propertyStatuses || []).map((value) => <option key={value}>{value}</option>)}</Select>
        <Select label="Sold-out by agent" value={draft.soldOutAgentId || ""} onChange={(value) => setDraft({ ...draft, soldOutAgentId: value })} disabled={!isSoldOutStatus(draft.status)}><option value="">Not sold-out</option>{users.filter((user) => user.active).map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</Select>
        <Input label="Original price" type="number" value={draft.originalPrice} onChange={(value) => setDraft({ ...draft, originalPrice: Number(value), price: Number(value) })} />
        <Input label="Promotion price" type="number" value={draft.promotionPrice} onChange={(value) => setDraft({ ...draft, promotionPrice: Number(value), price: Number(value) })} />
        <Input label="Commission %" type="number" value={draft.commissionRate} onChange={(value) => setDraft({ ...draft, commissionRate: Number(value), commissionAmount: Math.round(((draft.promotionPrice || draft.price || draft.originalPrice) * Number(value)) / 100) })} />
        <Input label="Commission amount" type="number" value={draft.commissionAmount} onChange={(value) => setDraft({ ...draft, commissionAmount: Number(value) })} />
        <Input label="Size" type="number" value={draft.size} onChange={(value) => setDraft({ ...draft, size: Number(value) })} />
        <Input label="Bedrooms" type="number" value={draft.bedrooms} onChange={(value) => setDraft({ ...draft, bedrooms: Number(value) })} />
        <Input label="Bathrooms" type="number" value={draft.bathrooms} onChange={(value) => setDraft({ ...draft, bathrooms: Number(value) })} />
        <Input label="Living rooms" type="number" value={draft.livingRooms} onChange={(value) => setDraft({ ...draft, livingRooms: Number(value) })} />
        <Input label="Kitchens" type="number" value={draft.kitchens} onChange={(value) => setDraft({ ...draft, kitchens: Number(value) })} />
        <Input label="Other rooms" type="number" value={draft.otherRooms} onChange={(value) => setDraft({ ...draft, otherRooms: Number(value) })} />
        <Input label="Car parking" type="number" value={draft.parkingCars} onChange={(value) => setDraft({ ...draft, parkingCars: Number(value) })} />
        <Checkbox label="Pet friendly" checked={draft.petFriendly} onChange={(value) => setDraft({ ...draft, petFriendly: value })} />
        <Checkbox label="Pool" checked={draft.pool} onChange={(value) => setDraft({ ...draft, pool: value })} />
        <Checkbox label="Fitness" checked={draft.fitness} onChange={(value) => setDraft({ ...draft, fitness: value })} />
        <Checkbox label="Co-working space" checked={draft.coWorkingSpace} onChange={(value) => setDraft({ ...draft, coWorkingSpace: value })} />
        <Checkbox label="Garden" checked={draft.garden} onChange={(value) => setDraft({ ...draft, garden: value })} />
        <MultiChoice label="View" values={propertyViewChoices(draft)} options={["Pool", "Garden"]} onChange={(values) => setDraft({ ...draft, view: textFromList(values), poolView: "", gardenView: "" })} />
        <MultiChoice label="Facilities" values={listFromText(draft.facilities)} options={masterData.facilities || []} onChange={(values) => setDraft({ ...draft, facilities: textFromList(values) })} />
        <Input label="Photos URL" value={draft.photos} onChange={(value) => setDraft({ ...draft, photos: value })} />
        <Input label="Timeline" value={draft.timeline} onChange={(value) => setDraft({ ...draft, timeline: value })} />
        <Select label="Visibility" value={draft.visibility} onChange={(value) => setDraft({ ...draft, visibility: value as Property["visibility"], publicVisible: value === "Public" })}><option>Private</option><option>Public</option></Select>
        <Input label="Public token" value={draft.publicToken} onChange={(value) => setDraft({ ...draft, publicToken: value })} />
        <Input label="Affiliate link" value={draft.affiliateLink} onChange={(value) => setDraft({ ...draft, affiliateLink: value })} />
        <TextArea label="Internal note" value={draft.internalNote} onChange={(value) => setDraft({ ...draft, internalNote: value })} />
        <div className="grid content-end gap-2">
          <button className="btn secondary" onClick={generateAffiliate} disabled={draft.visibility !== "Public"}><Link2 size={16} />Generate affiliate link</button>
          {draft.visibility === "Private" ? <p className="text-xs text-muted-foreground">Private properties use manually filled affiliate links.</p> : null}
          {publicUrl ? <a className="btn ghost" href={publicUrl} target="_blank" rel="noreferrer"><Eye size={16} />Open public URL</a> : null}
        </div>
        <button className="btn md:col-span-2" onClick={() => onSave({ ...draft, publicToken: token })}><FormattedMessage id="save" /></button>
      </FormGrid>
    </div>
  );
}

function DuplicateWarning({ count, visible, hiddenCount }: { count: number; visible: string[]; hiddenCount: number }) {
  return (
    <div className="rounded-md bg-[#d7b46a]/10 p-3 text-sm text-[#7a5a14]">
      <div className="flex items-start gap-2"><AlertTriangle size={17} /><div><p className="font-semibold">{count} possible duplicate{count === 1 ? "" : "s"} found</p><p>{visible.length ? `Accessible duplicate: ${visible.join(", ")}` : "Duplicate details are restricted by your access scope."}{hiddenCount ? ` ${hiddenCount} restricted record${hiddenCount === 1 ? "" : "s"} hidden.` : ""}</p></div></div>
    </div>
  );
}

function Timeline({ state, leadId, propertyId }: { state: CrmState; leadId?: string; propertyId?: string }) {
  const rows = [
    ...state.auditLogs.filter((log) => (leadId && log.entityId === leadId) || (propertyId && log.entityId === propertyId)).map((log) => ({ at: log.at, title: `${log.action} ${log.entity}`, detail: state.users.find((user) => user.id === log.by)?.name || log.by })),
    ...state.activities.filter((activity) => (!leadId || activity.leadId === leadId) && (!propertyId || activity.propertyId === propertyId)).map((activity) => ({ at: activity.dueAt, title: `${activity.type} follow-up`, detail: activity.status })),
    ...state.viewings.filter((viewing) => (!leadId || viewing.leadId === leadId) && (!propertyId || viewing.propertyId === propertyId)).map((viewing) => ({ at: viewing.startsAt, title: viewing.type, detail: viewing.status })),
    ...state.interestedProperties.filter((interest) => (!leadId || interest.leadId === leadId) && (!propertyId || interest.propertyId === propertyId)).map((interest) => ({ at: interest.updatedAt, title: "Interested property", detail: interest.status }))
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 8);
  return <Panel title="Timeline" icon={CalendarClock}>{!rows.length ? <EmptyState text="No timeline records yet." /> : <div className="grid gap-2">{rows.map((row, index) => <div className="rounded-md bg-background p-3 text-sm" key={`${row.title}-${index}`}><p className="font-medium">{row.title}</p><p className="text-muted-foreground">{dateTime(row.at)} / {row.detail}</p></div>)}</div>}</Panel>;
}

function SplitList({ title, action, query, setQuery, filters, children }: { title: string; action: JSX.Element; query: string; setQuery: (value: string) => void; filters?: JSX.Element; children: [JSX.Element, JSX.Element] }) {
  return <div className="grid min-w-0 gap-5 lg:grid-cols-[380px_1fr]"><Panel title={title} icon={Search} action={action}><div className="mb-4 grid gap-3"><Input placeholder="Search" value={query} onChange={setQuery} />{filters}</div>{children[0]}</Panel>{children[1]}</div>;
}

function Panel({ title, icon: Icon, action, children }: { title: string; icon: typeof LayoutDashboard; action?: JSX.Element; children: ReactNode }) {
  return <section className="min-w-0 rounded-lg bg-card p-5 shadow-sm ring-1 ring-[#ead8b6]/60"><div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div className="flex items-center gap-2"><Icon size={18} className="text-[#b58a34]" /><h2 className="font-semibold">{title}</h2></div>{action}</div>{children}</section>;
}

function FormGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

function Input({ label, value, onChange, placeholder, type = "text", disabled = false }: { label?: string; value: string | number; onChange: (value: string) => void; placeholder?: string; type?: string; disabled?: boolean }) {
  const input = <input className="field" type={type} value={value} placeholder={placeholder} disabled={disabled} onChange={(event) => onChange(event.target.value)} />;
  return label ? <label className="grid gap-1 text-sm font-medium text-muted-foreground"><span>{label}</span>{input}</label> : input;
}

function TextArea({ label, value, onChange, disabled = false }: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean }) {
  return <label className="grid gap-1 text-sm font-medium text-muted-foreground md:col-span-2 xl:col-span-3"><span>{label}</span><textarea className="field min-h-24" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} /></label>;
}

function CreatableInput({ label, value, options, onChange, disabled = false }: { label: string; value: string; options: string[]; onChange: (value: string) => void; disabled?: boolean }) {
  const listId = `list-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return <label className="grid gap-1 text-sm font-medium text-muted-foreground"><span>{label}</span><input className="field" list={listId} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />{options.length ? <datalist id={listId}>{options.map((option) => <option key={option} value={option} />)}</datalist> : null}</label>;
}

function Select({ label, value, onChange, children, disabled = false }: { label: string; value: string; onChange: (value: string) => void; children: ReactNode; disabled?: boolean }) {
  return <label className="grid gap-1 text-sm font-medium text-muted-foreground"><span>{label}</span><select className="field" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>{children}</select></label>;
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="check"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />{label}</label>;
}

function MultiChoice({ label, values, options, onChange }: { label: string; values: string[]; options: string[]; onChange: (values: string[]) => void }) {
  const choices = Array.from(new Set([...options, ...values])).filter(Boolean).sort(sortText);
  function toggle(value: string) {
    onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  }
  return (
    <div className="grid gap-2 text-sm font-medium text-muted-foreground md:col-span-2 xl:col-span-3">
      <span>{label}</span>
      <div className="flex flex-wrap gap-2 rounded-md bg-white/90 p-2 shadow-sm ring-1 ring-[#ead8b6]/60">
        {choices.map((choice) => (
          <button className={cn("soft-chip", values.includes(choice) && "active")} key={choice} type="button" onClick={() => toggle(choice)}>
            {choice}
          </button>
        ))}
      </div>
    </div>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return <button className="inline-grid h-9 w-9 place-items-center rounded-md bg-card text-muted-foreground transition hover:bg-muted hover:text-foreground" aria-label={label} title={label} onClick={onClick}>{children}</button>;
}

function EmptyState({ text }: { text: string }) {
  return <div className="rounded-md bg-[#fbf6ed] p-4 text-sm text-muted-foreground shadow-sm ring-1 ring-[#ead8b6]/60">{text}</div>;
}

export default App;
